import React, { useState, useEffect } from 'react';
import { LPComponent } from './lpComponentTypes';
import { getLeadForm } from '../../services/api';
import { loadEffectComponent, effectIntegration, effectPrimaryImage, effectImageState } from './revfxCatalogue';
import {
  // Check / validation
  CheckCircle, CheckSquare, BadgeCheck, ShieldCheck,
  // Business
  Briefcase, Building2, Store, CreditCard, Package, Box, Truck, Wallet, Receipt,
  // People / social
  User, Users, UserCheck, UserPlus, Heart, ThumbsUp, Smile, MessageCircle, MessageSquare,
  // Achievement
  Award, Star, Trophy, Crown, Shield,
  // Communication
  Phone, Mail, Send, Bell, AtSign, Globe, Headphones,
  // Time
  Clock, Calendar, CalendarCheck, Timer, AlarmClock,
  // Finance / analytics
  DollarSign, TrendingUp, TrendingDown, BarChart2, PieChart, Layers,
  // Tools / tech
  Wrench, Settings, Code, Monitor, Smartphone, Laptop, Cpu, Database, Server, Cloud, Wifi, Zap, PenTool, Pencil, Clipboard,
  // Navigation / location
  MapPin, Navigation, Compass, Map, Home, ArrowRight, Link,
  // Security
  Lock, Key, Eye, EyeOff, Fingerprint,
  // Content / media
  FileText, Image, Video, Camera, Mic, Music, BookOpen, GraduationCap,
  // Growth / nature
  Leaf, Sun, Sparkles, Flame, Activity,
  // Other
  Target, Flag, Download, Upload, RefreshCw, Search, Filter, ChevronRight,
  Scale, Handshake, Sword, Wand, OctagonX,
} from 'lucide-react';

export interface BrandLogoOption {
  url: string;
  label: string;
}

export interface BrandTheme {
  primaryColor: string;
  secondaryColor: string;
  accentColor?: string;
  fontFamily: string;
  /** Hosted URL of the brand's primary font file. Used to inject an @font-face
   *  rule into the published landing page HTML so the font actually loads for
   *  end-users (the family name alone falls back to system sans). */
  fontUrl?: string;
  logoUrl: string;
  companyName: string;
  logoOptions?: BrandLogoOption[];
}

interface LPComponentRendererProps {
  component: LPComponent;
  isSelected: boolean;
  brandTheme?: BrandTheme;
  onPropsChange?: (id: string, newProps: Record<string, any>) => void;
}

// ── Inline-editable text helper ─────────────────────────────────────────────

interface EditableTextProps {
  tag: keyof React.JSX.IntrinsicElements;
  html: string;
  editable: boolean;
  onSave: (html: string) => void;
  style?: React.CSSProperties;
  className?: string;
}

const EditableText: React.FC<EditableTextProps> = ({ tag: Tag, html, editable, onSave, style, className }: EditableTextProps) => {
  const ref = React.useRef<HTMLElement>(null);

  // Keep DOM in sync when props change externally (but don't overwrite mid-edit)
  React.useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      if (ref.current.innerHTML !== html) ref.current.innerHTML = html;
    }
  }, [html, editable]);

  return React.createElement(Tag as any, {
    ref,
    ...(editable ? {
      contentEditable: true,
      suppressContentEditableWarning: true,
      'data-lp-editable': '1',
      onBlur: () => { if (ref.current) onSave(ref.current.innerHTML); },
    } : { dangerouslySetInnerHTML: { __html: html } }),
    style: { ...style, outline: 'none', cursor: editable ? 'text' : undefined },
    className: editable ? `lp-et${className ? ' ' + className : ''}` : className,
    ...(editable ? {} : {}),
  });
};

// ── Shared style helpers ────────────────────────────────────────────────────


const sectionBase: React.CSSProperties = {
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

/** Inner content wrapper — constrains section content to 1200px on wide screens */
const innerWrap: React.CSSProperties = {
  maxWidth: '1200px',
  margin: '0 auto',
  width: '100%',
  boxSizing: 'border-box',
};

/** Computes background + text-colour overrides from universal BackgroundEditor props. */
/** Convert any YouTube/Vimeo share or watch URL to its embeddable equivalent.
 *  YouTube URLs are routed through the server-side wrapper which hides related-video
 *  suggestions on pause and end. Vimeo uses the direct player embed. */
function toEmbedUrl(url: string): string {
  try {
    // youtu.be/ID, youtube.com/watch?v=ID, youtube.com/shorts/ID, youtube.com/embed/ID
    const ytShort  = url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
    const ytWatch  = url.match(/youtube\.com\/watch\?(?:.*&)?v=([A-Za-z0-9_-]{11})/);
    const ytShorts = url.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/);
    const ytEmbed  = url.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{11})/);
    const ytId = (ytShort || ytWatch || ytShorts || ytEmbed)?.[1];
    if (ytId) return `/api/landing-pages/video-embed?v=${ytId}`;

    // vimeo.com/ID — direct embed (no suggestion overlay issue)
    const vimeo = url.match(/vimeo\.com\/(\d+)/);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  } catch { /* fall through */ }
  return url;
}

function buildSectionStyle(props: Record<string, any>): React.CSSProperties {
  const result: React.CSSProperties = {};
  if (props._bg_type === 'solid' && props._bg_color) {
    result.background = props._bg_color;
  } else if (props._bg_type === 'gradient' && props._bg_gradient_from && props._bg_gradient_to) {
    const angle = props._bg_gradient_angle || '135deg';
    result.background = `linear-gradient(${angle}, ${props._bg_gradient_from}, ${props._bg_gradient_to})`;
  }
  if (props._text_color) {
    result.color = props._text_color;
  }
  if (props._font_family) {
    result.fontFamily = props._font_family;
  }
  return result;
}

// ── Icon system ─────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ComponentType<{ size?: number; color?: string; style?: React.CSSProperties }>> = {
  // Check / validation
  'check-circle': CheckCircle, 'check': CheckSquare, 'check-square': CheckSquare,
  'badge-check': BadgeCheck, 'shield-check': ShieldCheck,
  // Business
  'briefcase': Briefcase, 'building': Building2, 'building-2': Building2,
  'store': Store, 'credit-card': CreditCard, 'package': Package, 'box': Box,
  'truck': Truck, 'wallet': Wallet, 'receipt': Receipt,
  // People / social
  'user': User, 'users': Users, 'user-check': UserCheck, 'user-plus': UserPlus,
  'heart': Heart, 'thumbs-up': ThumbsUp, 'smile': Smile,
  'message-circle': MessageCircle, 'message': MessageCircle, 'message-square': MessageSquare,
  // Achievement
  'award': Award, 'star': Star, 'trophy': Trophy, 'crown': Crown, 'shield': Shield,
  // Communication
  'phone': Phone, 'mail': Mail, 'send': Send, 'bell': Bell, 'at-sign': AtSign,
  'globe': Globe, 'headphones': Headphones,
  // Time
  'clock': Clock, 'timer': Timer, 'calendar': Calendar, 'calendar-check': CalendarCheck,
  'alarm-clock': AlarmClock,
  // Finance / analytics
  'dollar': DollarSign, 'dollar-sign': DollarSign, 'trending-up': TrendingUp,
  'trending-down': TrendingDown, 'chart': BarChart2, 'bar-chart': BarChart2,
  'bar-chart-2': BarChart2, 'pie-chart': PieChart, 'layers': Layers,
  // Tools / tech
  'wrench': Wrench, 'tool': Wrench, 'settings': Settings, 'code': Code,
  'monitor': Monitor, 'smartphone': Smartphone, 'laptop': Laptop, 'cpu': Cpu,
  'database': Database, 'server': Server, 'cloud': Cloud, 'wifi': Wifi,
  'zap': Zap, 'bolt': Zap, 'pen-tool': PenTool, 'pencil': Pencil, 'clipboard': Clipboard,
  // Navigation / location
  'map-pin': MapPin, 'location': MapPin, 'navigation': Navigation, 'compass': Compass,
  'map': Map, 'home': Home, 'arrow-right': ArrowRight, 'link': Link,
  // Security
  'lock': Lock, 'key': Key, 'eye': Eye, 'eye-off': EyeOff, 'fingerprint': Fingerprint,
  // Content / media
  'file': FileText, 'file-text': FileText, 'image': Image, 'video': Video,
  'camera': Camera, 'mic': Mic, 'music': Music, 'book-open': BookOpen,
  'graduation-cap': GraduationCap,
  // Growth / nature
  'leaf': Leaf, 'sun': Sun, 'sparkles': Sparkles, 'flame': Flame, 'activity': Activity,
  // Other
  'target': Target, 'flag': Flag, 'download': Download, 'upload': Upload,
  'refresh': RefreshCw, 'search': Search, 'filter': Filter, 'chevron-right': ChevronRight,
  'scale': Scale, 'handshake': Handshake, 'sword': Sword, 'wand': Wand, 'octagon-x': OctagonX,
};

function renderLpIcon(name: string, size = 20, color = '#2177FF'): React.ReactNode {
  const normalised = (name || '').toLowerCase().trim();
  const IconComp = ICON_MAP[normalised];
  if (IconComp) return <IconComp size={size} color={color} />;
  // Fallback: first letter in a coloured circle
  return (
    <span style={{ fontWeight: 700, fontSize: size * 0.65, color }}>
      {name ? name.charAt(0).toUpperCase() : '◆'}
    </span>
  );
}

// ── Individual renderers ────────────────────────────────────────────────────

function HeroTextContent({ props, primary, secondary, isSplit, isEditable, onTextSave }: { props: Record<string, any>; primary: string; secondary: string; isSplit?: boolean; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  const save = onTextSave ?? (() => {});
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: isSplit ? 'flex-start' : 'center',
      textAlign: isSplit ? 'left' : 'center', gap: '16px', flex: isSplit ? '0 0 55%' : undefined,
    }}>
      {props.badge && (
        <span style={{
          display: 'inline-block',
          background: 'rgba(255,255,255,0.15)',
          border: '1px solid rgba(255,255,255,0.25)',
          borderRadius: '9999px',
          padding: '4px 16px',
          fontSize: '12px',
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase' as const,
        }}>
          {props.badge}
        </span>
      )}
      <EditableText tag="h1" html={props.headline || 'Your Headline Here'} editable={!!isEditable}
        onSave={(v) => save({ ...props, headline: v })}
        style={{ margin: 0, fontSize: '2.5rem', fontWeight: 800, lineHeight: 1.15 }}
      />
      {(props.subheadline || isEditable) && (
        <EditableText tag="p" html={props.subheadline || ''} editable={!!isEditable}
          onSave={(v) => save({ ...props, subheadline: v })}
          style={{ margin: 0, fontSize: '1.1rem', opacity: 0.82, lineHeight: 1.6, maxWidth: isSplit ? undefined : '560px' }}
        />
      )}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px', justifyContent: isSplit ? 'flex-start' : 'center' }}>
        {props.cta_text && (
          <a href={props.cta_url || '#'} className="lp-btn" style={{
            display: 'inline-block',
            background: props.button_color || secondary,
            color: '#ffffff',
            padding: '14px 28px',
            borderRadius: '9999px',
            textDecoration: 'none',
            fontWeight: 700,
            fontSize: '1rem',
            boxShadow: `0 4px 20px ${(props.button_color || secondary)}44`,
          }}>
            {props.cta_text}
          </a>
        )}
        {props.secondary_cta_text && (
          <a href={props.secondary_cta_url || '#'} className="lp-btn lp-btn-ghost" style={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.15)',
            color: '#ffffff',
            padding: '14px 28px',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            border: '1px solid rgba(255,255,255,0.35)',
          }}>
            {props.secondary_cta_text}
          </a>
        )}
      </div>
    </div>
  );
}

function HeroSplitForm({ props, secondary, isEditable }: { props: Record<string, any>; secondary: string; isEditable?: boolean }) {
  const [linkedForm, setLinkedForm] = useState<any>(null);
  const [loadingForm, setLoadingForm] = useState(false);

  useEffect(() => {
    const formId = props.form_id;
    if (!formId) {
      setLinkedForm(null);
      return;
    }
    setLoadingForm(true);
    getLeadForm(formId)
      .then((data) => setLinkedForm(data))
      .catch(() => setLinkedForm(null))
      .finally(() => setLoadingForm(false));
  }, [props.form_id]);

  const formFields: Array<{ id: string; label: string; type: string; placeholder: string; required: boolean; options?: string[] }> | null =
    linkedForm?.fields && linkedForm.fields.length > 0
      ? linkedForm.fields
      : null;

  // Prefer the hero-section-specific override so the form button text is
  // independent of the main CTA button (props.cta_text). Falls back to the
  // linked form's own label, then to props.cta_text for backwards compatibility
  // with pages saved before form_submit_text existed.
  const submitLabel = props.form_submit_text || linkedForm?.submit_button_text || props.cta_text || 'Submit';
  const successMessage = linkedForm?.success_message || '';
  const formName = linkedForm?.name || '';

  const inputStyle: React.CSSProperties = {
    display: 'block', width: '100%', boxSizing: 'border-box',
    marginBottom: '12px', padding: '10px 14px',
    border: '1px solid #e2e8f0', borderRadius: '6px',
    fontSize: '0.875rem', color: '#64748b', background: '#f8fafc',
  };

  return (
    <form
      id="hero-form"
      data-form-id={props.form_id || ''}
      data-success-message={successMessage}
      action="javascript:void(0)"
      style={{
        background: 'rgba(255,255,255,0.97)',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
        minWidth: 0,
        margin: 0,
      }}
    >
      <h3 style={{ margin: '0 0 8px', color: '#0f172a', fontSize: '1.1rem', fontWeight: 700 }}>
        {props.form_title || 'Book Your Free Consultation'}
      </h3>
      {props.form_id && formName && (
        <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '-4px 0 16px', textAlign: 'left' }}>
          Linked form: {formName}
        </p>
      )}
      {!props.form_id && <div style={{ marginBottom: '20px' }} />}
      {loadingForm ? (
        <p style={{ fontSize: '0.875rem', color: '#64748b', textAlign: 'center' }}>Loading form fields…</p>
      ) : formFields ? (
        formFields.map((field) => (
          <div key={field.id}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>
              {field.label}{field.required ? ' *' : ''}
            </label>
            {field.type === 'textarea' ? (
              <textarea
                name={field.id}
                placeholder={field.placeholder || field.label}
                required={field.required}
                readOnly
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              />
            ) : field.type === 'select' ? (
              <select
                name={field.id}
                required={field.required}
                disabled
                style={inputStyle}
              >
                <option value="">-- Select --</option>
                {(field.options || []).map((opt: string) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : (
              <input
                name={field.id}
                type={field.type || 'text'}
                placeholder={field.placeholder || field.label}
                required={field.required}
                readOnly
                style={inputStyle}
              />
            )}
          </div>
        ))
      ) : (
        ['Full Name', 'Email Address', 'Phone Number'].map((field) => (
          <input
            key={field}
            placeholder={field}
            readOnly
            style={inputStyle}
          />
        ))
      )}
      <button
        type="submit"
        className="lp-btn"
        style={{
          width: '100%', padding: '12px',
          background: secondary, color: '#fff',
          border: 'none', borderRadius: '9999px',
          fontWeight: 700, fontSize: '0.95rem',
          cursor: props.form_id ? 'pointer' : 'default',
          marginTop: '4px',
        }}
      >
        {submitLabel}
      </button>
      {props.form_id && isEditable && (
        <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#94a3b8', margin: '8px 0 0' }}>
          (submits live on the published page)
        </p>
      )}
    </form>
  );
}

function HeroRenderer({ props, primary, secondary, brandTheme, isEditable, onTextSave }: { props: Record<string, any>; primary: string; secondary: string; brandTheme?: BrandTheme; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  const bgStyle: React.CSSProperties =
    props.background_style === 'solid'
      ? { background: primary }
      : props.background_style === 'image' && props.background_image
      ? {
          backgroundImage: `url(${props.background_image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : {
          background:
            `linear-gradient(135deg, ${primary}dd 0%, #0f172a 60%, #1a2a4a 100%)`,
        };

  const layout = props.layout || 'centered';

  const heroSectionStyle = buildSectionStyle(props);

  if (layout === 'split_form') {
    return (
      <div className="lps lps-hero lps-hero-split" style={{ ...sectionBase, ...bgStyle, color: '#ffffff', padding: '60px 48px', minHeight: '300px', ...heroSectionStyle }}>
        <div className="lps-hero-grid" style={{
          display: 'grid',
          gridTemplateColumns: '55fr 45fr',
          gap: '40px',
          alignItems: 'center',
          maxWidth: '1200px',
          margin: '0 auto',
        }}>
          <HeroTextContent props={props} primary={primary} secondary={secondary} isSplit isEditable={isEditable} onTextSave={onTextSave} />
          <HeroSplitForm props={props} secondary={secondary} isEditable={isEditable} />
        </div>
      </div>
    );
  }

  if (layout === 'split_image') {
    return (
      <div className="lps lps-hero" style={{ ...sectionBase, ...bgStyle, color: '#ffffff', padding: '60px 48px', minHeight: '300px', ...heroSectionStyle }}>
        <div className="lps-hero-grid" style={{
          display: 'grid',
          gridTemplateColumns: '55fr 45fr',
          gap: '40px',
          alignItems: 'center',
          maxWidth: '1200px',
          margin: '0 auto',
        }}>
          <HeroTextContent props={props} primary={primary} secondary={secondary} isSplit isEditable={isEditable} onTextSave={onTextSave} />
          {props.split_image ? (
            <div style={{ minWidth: 0 }}>
              <img src={props.split_image} alt="" style={{ width: '100%', borderRadius: '12px', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }} />
            </div>
          ) : (
            <div style={{
              minHeight: '220px', minWidth: 0,
              background: 'rgba(255,255,255,0.1)', border: '2px dashed rgba(255,255,255,0.3)',
              borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem',
            }}>
              Upload image in properties →
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default: centered layout
  return (
    <div className="lps lps-hero" style={{
      ...sectionBase, ...bgStyle,
      color: '#ffffff', padding: '80px 48px', minHeight: '300px',
      display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '20px',
      ...heroSectionStyle,
    }}>
      <HeroTextContent props={props} primary={primary} secondary={secondary} isEditable={isEditable} onTextSave={onTextSave} />
    </div>
  );
}

function FeaturesRenderer({ props, primary, isEditable, onTextSave }: { props: Record<string, any>; primary: string; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  const items: Array<{ icon: string; title: string; description: string }> =
    props.items || [];
  const save = onTextSave ?? (() => {});
  // Section-level overrides (set in the properties panel). Fall back to the
  // brand primary for icons and the default dark/muted text when unset.
  const iconColor = props._icon_color || primary;
  const textColor = props._text_color;
  const titleColor = textColor || '#0f172a';
  // When a custom text colour is set, dim descriptions slightly to keep the
  // title→body hierarchy regardless of the chosen colour.
  const descStyle = textColor ? { color: textColor, opacity: 0.8 } : { color: '#64748b' };
  return (
    <div
      className="lps lps-features"
      style={{
        ...sectionBase,
        background: '#ffffff',
        padding: '60px 0',
        ...buildSectionStyle(props),
      }}
    >
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px' }}>
      {(props.title || isEditable) && (
        <EditableText tag="h2" html={props.title || ''} editable={!!isEditable}
          onSave={(v) => save({ ...props, title: v })}
          style={{ textAlign: 'center', marginBottom: '40px', fontSize: '1.8rem', fontWeight: 700, color: titleColor }}
        />
      )}
      {items.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
          No features added yet — edit in the properties panel.
        </p>
      ) : (
        <div
          className="lps-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '24px',
          }}
        >
          {items.map((item, i) => (
            <div
              key={i}
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '28px 24px',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: `${iconColor}1a`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '14px',
                  fontSize: '18px',
                  color: iconColor,
                  fontWeight: 700,
                }}
              >
                {renderLpIcon(item.icon || 'check-circle', 20, iconColor)}
              </div>
              <EditableText tag="h4" html={item.title || ''} editable={!!isEditable}
                onSave={(v) => { const it = items.map((x,j) => j===i?{...x,title:v}:x); save({...props,items:it}); }}
                style={{ margin: '0 0 8px', fontSize: '1rem', fontWeight: 700, color: titleColor }}
              />
              <EditableText tag="p" html={item.description || ''} editable={!!isEditable}
                onSave={(v) => { const it = items.map((x,j) => j===i?{...x,description:v}:x); save({...props,items:it}); }}
                style={{ margin: 0, fontSize: '0.875rem', ...descStyle, lineHeight: 1.5 }}
              />
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

function ServicesRenderer({ props, primary, isEditable, onTextSave }: { props: Record<string, any>; primary: string; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  const items: Array<{ icon: string; title: string; description: string }> =
    props.items || [];
  const save = onTextSave ?? (() => {});
  // Section-level overrides (properties panel). Services use a solid icon tile,
  // so _icon_color drives the tile background (glyph stays white).
  const iconColor = props._icon_color || primary;
  const textColor = props._text_color;
  const titleColor = textColor || '#0f172a';
  const descStyle = textColor ? { color: textColor, opacity: 0.8 } : { color: '#64748b' };
  return (
    <div
      className="lps lps-services"
      style={{
        ...sectionBase,
        background: '#f8fafc',
        padding: '60px 0',
        ...buildSectionStyle(props),
      }}
    >
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px' }}>
      {(props.title || isEditable) && (
        <EditableText tag="h2" html={props.title || ''} editable={!!isEditable}
          onSave={(v) => save({ ...props, title: v })}
          style={{ textAlign: 'center', marginBottom: '40px', fontSize: '1.8rem', fontWeight: 700, color: titleColor }}
        />
      )}
      {items.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
          No services added yet — edit in the properties panel.
        </p>
      ) : (
        <div
          className="lps-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '24px',
            maxWidth: '960px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          {items.map((item, i) => (
            <div
              key={i}
              style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '32px 24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: iconColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '20px',
                }}
              >
                {renderLpIcon(item.icon || 'briefcase', 22, '#ffffff')}
              </div>
              <EditableText tag="h3" html={item.title || ''} editable={!!isEditable}
                onSave={(v) => { const it = items.map((x,j) => j===i?{...x,title:v}:x); save({...props,items:it}); }}
                style={{ margin: '0 0 10px', fontSize: '1.1rem', fontWeight: 700, color: titleColor }}
              />
              <EditableText tag="p" html={item.description || ''} editable={!!isEditable}
                onSave={(v) => { const it = items.map((x,j) => j===i?{...x,description:v}:x); save({...props,items:it}); }}
                style={{ margin: 0, fontSize: '0.875rem', ...descStyle, lineHeight: 1.6 }}
              />
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

function StatsRenderer({ props, primary, isEditable, onTextSave }: { props: Record<string, any>; primary: string; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  const items: Array<{ value: string; label: string }> = props.items || [];
  const save = onTextSave ?? (() => {});
  return (
    <div
      className="lps lps-stats"
      style={{
        ...sectionBase,
        background: `linear-gradient(135deg, ${primary}ee 0%, ${primary}bb 100%)`,
        color: '#ffffff',
        padding: '56px 0',
        ...buildSectionStyle(props),
      }}
    >
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px' }}>
      <div
        className="lps-stats-row"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '48px',
        }}
      >
        {items.map((item, i) => (
          <div key={i} style={{ textAlign: 'center', minWidth: '120px' }}>
            <EditableText tag="div" html={item.value || ''} editable={!!isEditable}
              onSave={(v) => { const it = items.map((x,j) => j===i?{...x,value:v}:x); save({...props,items:it}); }}
              style={{ fontSize: '2.8rem', fontWeight: 800, color: props.value_color || primary, lineHeight: 1 }}
            />
            <EditableText tag="div" html={item.label || ''} editable={!!isEditable}
              onSave={(v) => { const it = items.map((x,j) => j===i?{...x,label:v}:x); save({...props,items:it}); }}
              style={{ fontSize: '0.85rem', fontWeight: 500, opacity: 0.75, marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}
            />
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

function LpTextRenderer({ props, isEditable, onTextSave }: { props: Record<string, any>; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  // Plain text content block. Deliberately uses the dedicated `.lps-text` class
  // (NOT `.lps-process`/`.lps-process-row`) so it never inherits sibling sections'
  // hardcoded colours/borders. It sets no background or text colour of its own —
  // colour is inherited from the published-page base styles (design-system rule:
  // no hardcoded colours). buildSectionStyle still applies any user bg/text override.
  const save = onTextSave ?? (() => {});
  const align = props.align === 'center' ? 'center' : 'left';
  const maxWidth = props.max_width === 'wide' ? '1100px' : '720px';
  const hasHeading = !!(props.heading || '').trim();
  return (
    <div
      className="lps lps-text"
      // Default to a white content-section background (matches Features/Services) so
      // the block conforms to the style guide instead of being transparent. The
      // Section background control (buildSectionStyle) overrides this when set.
      style={{ ...sectionBase, background: '#ffffff', padding: '48px 0', ...buildSectionStyle(props) }}
    >
      <div className="lps-inner" style={{ ...innerWrap, maxWidth, padding: '0 48px', textAlign: align }}>
        {(hasHeading || isEditable) && (
          <EditableText tag="h2" html={props.heading || ''} editable={!!isEditable}
            onSave={(v) => save({ ...props, heading: v })}
            style={{ margin: '0 0 16px', fontSize: '1.8rem', fontWeight: 700, lineHeight: 1.25 }}
          />
        )}
        <EditableText tag="div" html={props.body || ''} editable={!!isEditable}
          onSave={(v) => save({ ...props, body: v })}
          style={{ fontSize: '1.05rem', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
        />
      </div>
    </div>
  );
}

// Catches render/commit errors thrown by an effect component (e.g. a required prop
// the generic editor can't supply, like Typewriter.phrases) so a single misbehaving
// effect degrades to overlay-only instead of crashing the whole editor. Keyed by
// effectId at the call site so switching effects clears a prior failure.
class EffectErrorBoundary extends React.Component<{ children: React.ReactNode; fallback?: React.ReactNode }, { failed: boolean }> {
  constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch() {
    /* swallow — fall back to the image (if any), else nothing; overlay still renders */
  }
  render() {
    return this.state.failed ? (this.props.fallback ?? null) : this.props.children;
  }
}

// ── RevFX effect wrapper ────────────────────────────────────────────────────
// Lazily loads an effect module (per-effect code-split chunk; `three` only pulled
// for WebGL effects) and renders its component as an absolute-inset overlay. If the
// effect id is unknown, the import fails, or the effect throws on render, it renders
// nothing — the section degrades to a plain coloured box with a readable overlay
// rather than a broken page.
// Builds the effect element per its integration kind: strips synthetic UI keys
// (__img/__before/__after/__align/__fontSize) and feeds the effect its real inputs
// — an image child (Revealer/MouseTrap/ExplodingReveal), a before/after pair
// (BeforeAfter), centered typed text (Typewriter), or a filled background layer.
// Shared shape with widgets/src/effects/AimeEffect.tsx (kept in sync by hand).
export function buildEffectElement(
  Comp: React.ComponentType<any>,
  effectId: string,
  effectProps: Record<string, any>,
  overlayColor?: string,
): React.ReactNode {
  const integ = effectIntegration(effectId);
  const { __img, __before, __after, __align, __fontSize, __imgAlign, __imgFit, __items, __color, __text, ...real } = effectProps || {};
  const fill: React.CSSProperties = { position: 'absolute', inset: 0, width: '100%', height: '100%' };
  const imgStyle: React.CSSProperties = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
  const objPos = (a: string): string => a === 'left' ? 'left center' : a === 'right' ? 'right center' : a === 'top' ? 'center top' : a === 'bottom' ? 'center bottom' : 'center';

  // FilmStrip takes media-item objects; map the uploaded image URLs into them.
  if (effectId === 'filmstrip') {
    const items = (Array.isArray(__items) ? __items : []).map((u: string) => ({ src: u, type: 'image' }));
    return <Comp {...real} items={items} style={fill} />;
  }
  // CarouselX renders its children as slides — feed it the uploaded images.
  if (effectId === 'carouselx') {
    const slides = Array.isArray(__items) ? __items : [];
    return (
      <Comp {...real} style={fill}>
        {slides.map((u: string, i: number) => (
          <div key={i} style={{ width: '100%', height: '100%' }}><img src={u} alt="" style={imgStyle} /></div>
        ))}
      </Comp>
    );
  }
  // PolyFold can decorate top, bottom, or both edges (two instances for 'both').
  if (effectId === 'polyfold' && real.edge === 'both') {
    const { edge, ...rest } = real;
    return (
      <>
        <Comp {...rest} edge="top" style={fill} />
        <Comp {...rest} edge="bottom" style={fill} />
      </>
    );
  }
  // Duotone tints a child image; render it as children so we can control how the
  // image fills the span (object-position) — its `src` prop ignores positioning.
  if (effectId === 'duotone') {
    const { src, ...rest } = real;
    const pos = __imgAlign === 'left' ? 'left center' : __imgAlign === 'right' ? 'right center' : 'center';
    return <Comp {...rest} style={fill}>{src ? <img src={src} alt="" style={{ ...imgStyle, objectPosition: pos }} /> : null}</Comp>;
  }
  // Whiteboard "hand-draws" its children — feed it the panel's text as styled content.
  if (effectId === 'whiteboard') {
    const justify = __align === 'left' ? 'flex-start' : __align === 'right' ? 'flex-end' : 'center';
    return (
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: justify, padding: '0 6%' }}>
        <Comp {...real}>
          <div style={{ fontSize: `${__fontSize || 48}px`, fontWeight: 800, lineHeight: 1.2, color: __color || overlayColor || '#ffffff', textAlign: (__align || 'center') as any, whiteSpace: 'pre-line' }}>
            {__text || ''}
          </div>
        </Comp>
      </div>
    );
  }

  if (integ.kind === 'childImage') {
    const childStyle: React.CSSProperties = {
      width: '100%', height: '100%', display: 'block',
      objectFit: __imgFit === 'contain' ? 'contain' : 'cover',
      objectPosition: objPos(__imgAlign),
    };
    return <Comp {...real} style={fill}>{__img ? <img src={__img} alt="" style={childStyle} /> : null}</Comp>;
  }
  if (integ.kind === 'beforeAfter') {
    return (
      <Comp
        {...real}
        style={fill}
        before={__before ? <img src={__before} alt="" style={imgStyle} /> : null}
        after={__after ? <img src={__after} alt="" style={imgStyle} /> : null}
      />
    );
  }
  if (integ.kind === 'text') {
    const justify = __align === 'left' ? 'flex-start' : __align === 'right' ? 'flex-end' : 'center';
    // Drop blank lines only at render (the editor keeps them so newlines can be typed).
    const phrases = Array.isArray(real.phrases) ? real.phrases.filter((s: string) => s && s.length) : real.phrases;
    return (
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: justify, padding: '0 6%' }}>
        <Comp
          {...real}
          phrases={phrases}
          style={{ textAlign: (__align || 'center') as any, fontSize: `${__fontSize || 48}px`, fontWeight: 800, lineHeight: 1.15, color: __color || overlayColor || '#ffffff' }}
        />
      </div>
    );
  }
  return <Comp {...real} style={fill} />;
}

function RevfxEffect({ effectId, effectProps, overlayColor }: { effectId: string; effectProps: Record<string, any>; overlayColor?: string }) {
  const [Comp, setComp] = React.useState<React.ComponentType<any> | null>(null);
  React.useEffect(() => {
    let alive = true;
    setComp(null);
    loadEffectComponent(effectId)
      .then((C) => { if (alive) setComp(() => C); })
      .catch(() => { if (alive) setComp(null); });
    return () => { alive = false; };
  }, [effectId]);
  if (!Comp) return null;
  const isText = effectIntegration(effectId).kind === 'text';
  const fbImg = effectPrimaryImage(effectId, effectProps);
  const fallback = fbImg ? <img src={fbImg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} /> : null;
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 0 }} {...(isText ? {} : { 'aria-hidden': 'true' as const })}>
      <EffectErrorBoundary key={effectId} fallback={fallback}>
        {buildEffectElement(Comp, effectId, effectProps, overlayColor)}
      </EffectErrorBoundary>
    </div>
  );
}

function LpEffectRenderer({ props, primary, isEditable, onTextSave }: { props: Record<string, any>; primary: string; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  const save = onTextSave ?? (() => {});
  const effectId = props.effect_id || 'snow';
  const effectProps = (props.effect_props && typeof props.effect_props === 'object') ? props.effect_props : {};
  const minHeight = typeof props.min_height === 'number' ? props.min_height : 480;
  const halign: 'left' | 'center' | 'right' = props.overlay_align === 'left' ? 'left' : props.overlay_align === 'right' ? 'right' : 'center';
  const halignFlex = halign === 'left' ? 'flex-start' : halign === 'right' ? 'flex-end' : 'center';
  const valignFlex = props.overlay_valign === 'top' ? 'flex-start' : props.overlay_valign === 'bottom' ? 'flex-end' : 'center';
  const ctaAlign: 'left' | 'center' | 'right' = props.overlay_cta_align === 'left' ? 'left' : props.overlay_cta_align === 'right' ? 'right' : 'center';
  const showCta = props.overlay_show_cta ?? !!props.overlay_cta_text;
  const color = props.overlay_color || '#ffffff';
  const imgState = effectImageState(effectId, effectProps);
  const showImgHint = !!isEditable && imgState.needs && !imgState.has;
  return (
    <div
      className="lps lps-effect"
      data-aime-effect={effectId}
      data-effect-props={JSON.stringify(effectProps)}
      style={{
        // display:block is essential: the published <aime-effect> is a custom
        // element (default display:inline), and inline elements ignore min-height,
        // so without this the published section collapses to 0 height and the effect
        // (absolute inset:0) has nothing to fill → blank.
        display: 'block',
        position: 'relative',
        minHeight: `${minHeight}px`,
        overflow: 'hidden',
        ...buildSectionStyle(props),
      }}
    >
      <React.Suspense fallback={null}>
        <RevfxEffect effectId={effectId} effectProps={effectProps} overlayColor={color} />
      </React.Suspense>
      {/* Editor-only hint: this effect needs an image that hasn't been uploaded yet,
          so it would otherwise look blank. Not serialized to the published page. */}
      {showImgHint && (
        <div
          data-lp-editor-only="1"
          style={{
            position: 'absolute', inset: 0, zIndex: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: '8px', textAlign: 'center', padding: '0 24px',
            border: '2px dashed rgba(255,255,255,0.35)', color: 'rgba(255,255,255,0.85)',
            fontSize: '0.95rem', fontWeight: 600, pointerEvents: 'none',
          }}
        >
          <Image size={28} style={{ opacity: 0.8 }} />
          Upload an image in the properties panel to see this effect
        </div>
      )}
      {/* Overlay — the stable .lps-effect-overlay class is what the publish swap
          extracts as the <aime-effect> children. It fills the section (absolute) and
          self-positions its content block via flex (vertical + horizontal), so the
          alignment is identical in the editor and the published runtime.
          pointer-events:none lets clicks reach interactive effects except on content. */}
      <div
        className="lps-effect-overlay"
        style={{
          position: 'absolute', inset: 0, zIndex: 1,
          display: 'flex', flexDirection: 'column',
          justifyContent: valignFlex, alignItems: halignFlex,
          padding: '40px 48px', boxSizing: 'border-box',
          textAlign: halign, color, pointerEvents: 'none',
        }}
      >
        <div style={{ width: '100%', maxWidth: '900px', pointerEvents: 'auto' }}>
          {(props.overlay_heading || isEditable) && (
            <EditableText tag="h2" html={props.overlay_heading || ''} editable={!!isEditable}
              onSave={(v) => save({ ...props, overlay_heading: v })}
              style={{ margin: '0 0 12px', fontSize: '2.4rem', fontWeight: 800, lineHeight: 1.15, color }}
            />
          )}
          {(props.overlay_subheading || isEditable) && (
            <EditableText tag="p" html={props.overlay_subheading || ''} editable={!!isEditable}
              onSave={(v) => save({ ...props, overlay_subheading: v })}
              style={{ margin: '0 0 20px', fontSize: '1.15rem', lineHeight: 1.5, color, opacity: 0.95 }}
            />
          )}
          {showCta && (props.overlay_cta_text || isEditable) && (
            <div style={{ textAlign: ctaAlign }}>
              <a
                href={props.overlay_cta_url || '#'}
                className="lp-btn"
                style={{ display: 'inline-block', padding: '14px 28px', borderRadius: '9999px', background: props.overlay_cta_color || primary, color: '#ffffff', fontWeight: 700, textDecoration: 'none' }}
                onClick={(e) => { if (isEditable) e.preventDefault(); }}
              >
                <EditableText tag="span" html={props.overlay_cta_text || ''} editable={!!isEditable}
                  onSave={(v) => save({ ...props, overlay_cta_text: v })}
                />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProcessRenderer({ props, primary, isEditable, onTextSave }: { props: Record<string, any>; primary: string; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  // Support both old format (string[]) and new format ({title, description}[])
  const rawSteps = props.steps || [];
  const steps: Array<{ title: string; description: string }> = rawSteps.map((s: any) =>
    typeof s === 'string' ? { title: s, description: '' } : { title: s.title || '', description: s.description || '' }
  );
  const save = onTextSave ?? (() => {});
  return (
    <div
      className="lps lps-process"
      style={{
        ...sectionBase,
        background: '#ffffff',
        padding: '60px 0',
        ...buildSectionStyle(props),
      }}
    >
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px' }}>
      {(props.title || isEditable) && (
        <EditableText tag="h2" html={props.title || ''} editable={!!isEditable}
          onSave={(v) => save({ ...props, title: v })}
          style={{ textAlign: 'center', marginBottom: '48px', fontSize: '1.8rem', fontWeight: 700, color: '#0f172a' }}
        />
      )}
      <div
        className="lps-process-row"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '0',
        }}
      >
        {steps.map((step, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flex: '1 1 140px',
              maxWidth: '220px',
              position: 'relative',
              padding: '0 8px',
            }}
          >
            {/* Connector line */}
            {i < steps.length - 1 && (
              <div
                style={{
                  position: 'absolute',
                  top: '20px',
                  left: '60%',
                  right: '-40%',
                  height: '2px',
                  background: '#e2e8f0',
                  zIndex: 0,
                }}
              />
            )}
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: primary,
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '1rem',
                position: 'relative',
                zIndex: 1,
              }}
            >
              {i + 1}
            </div>
            <div className="lps-process-text">
              <EditableText tag="h4" html={step.title || ''} editable={!!isEditable}
                onSave={(v) => { const st = steps.map((s,j) => j===i ? {...s, title: v} : s); save({...props, steps: st}); }}
                style={{ marginTop: '12px', textAlign: 'center', fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}
              />
              {(step.description || isEditable) && (
                <EditableText tag="p" html={step.description || ''} editable={!!isEditable}
                  onSave={(v) => { const st = steps.map((s,j) => j===i ? {...s, description: v} : s); save({...props, steps: st}); }}
                  style={{ marginTop: '6px', textAlign: 'center', fontSize: '0.8rem', fontWeight: 400, color: '#64748b', lineHeight: 1.5 }}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

function TestimonialsRenderer({ props, primary, isEditable, onTextSave }: { props: Record<string, any>; primary: string; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  const items: Array<{ quote: string; author: string; role: string; company?: string; avatar_url?: string }> =
    props.items || [];
  const save = onTextSave ?? (() => {});
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (items.length <= 1 || isEditable) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 5000);
    return () => clearInterval(t);
  }, [items.length, isEditable]);

  return (
    <div className="lps lps-testimonials" style={{ ...sectionBase, background: '#f1f5f9', padding: '60px 0', ...buildSectionStyle(props) }}>
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px' }}>
      {props.title && (
        <EditableText tag="h2" html={props.title} editable={!!isEditable}
          onSave={(v) => save({ ...props, title: v })}
          style={{ textAlign: 'center', marginBottom: '40px', fontSize: '1.8rem', fontWeight: 700, color: '#0f172a' }}
        />
      )}
      {items.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
          No testimonials yet — edit in the properties panel.
        </p>
      ) : (
        <div data-aime-testimonials="" style={{ maxWidth: '720px', margin: '0 auto' }}>
          <style>{`
            @keyframes lp-testimonial-slide-in {
              from { transform: translateX(60px); opacity: 0; }
              to   { transform: translateX(0);    opacity: 1; }
            }
          `}</style>
          {items.map((t, i) => (
            <div
              key={i}
              data-testimonial=""
              style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                padding: '40px 48px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                textAlign: 'center',
                display: i === idx ? 'block' : 'none',
                animation: i === idx ? 'lp-testimonial-slide-in 0.45s ease' : undefined,
              }}
            >
              <EditableText tag="p" html={`"${t.quote}"`} editable={!!isEditable}
                onSave={(v) => { const stripped = v.replace(/^"|"$/g, ''); const newItems = items.map((x,j) => j===i ? {...x, quote: stripped} : x); save({...props, items: newItems}); }}
                style={{ margin: '0 0 28px', fontStyle: 'italic', color: '#475569', lineHeight: 1.7, fontSize: '1.1rem' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', justifyContent: 'center' }}>
                {t.avatar_url ? (
                  <img
                    src={t.avatar_url}
                    alt={t.author}
                    style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <div style={{
                    width: '52px', height: '52px', borderRadius: '50%', flexShrink: 0,
                    background: primary, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '1.1rem',
                  }}>
                    {t.author ? t.author.charAt(0).toUpperCase() : '?'}
                  </div>
                )}
                <div style={{ textAlign: 'left' }}>
                  <EditableText tag="div" html={t.author || ''} editable={!!isEditable}
                    onSave={(v) => { const newItems = items.map((x,j) => j===i ? {...x, author: v} : x); save({...props, items: newItems}); }}
                    style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}
                  />
                  <EditableText tag="div" html={`${t.role || ''}${t.company ? ` · ${t.company}` : ''}`} editable={!!isEditable}
                    onSave={(v) => { const newItems = items.map((x,j) => j===i ? {...x, role: v} : x); save({...props, items: newItems}); }}
                    style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '2px' }}
                  />
                </div>
              </div>
            </div>
          ))}
          {/* Dot indicators */}
          {items.length > 1 && (
            <div data-aime-testimonial-dots="" style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
              {items.map((_, i) => (
                <div
                  key={i}
                  onClick={() => setIdx(i)}
                  style={{
                    width: i === idx ? '24px' : '8px',
                    height: '8px',
                    borderRadius: '9999px',
                    background: i === idx ? primary : '#cbd5e1',
                    cursor: 'pointer',
                    transition: 'width 0.3s, background 0.3s',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

function FaqRenderer({ props, primary, isEditable, onTextSave }: { props: Record<string, any>; primary: string; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  const items: Array<{ question: string; answer: string }> = props.items || [];
  const save = onTextSave ?? (() => {});
  return (
    <div
      className="lps lps-faq"
      style={{
        ...sectionBase,
        background: '#ffffff',
        padding: '60px 0',
        ...buildSectionStyle(props),
      }}
    >
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px' }}>
      {props.title && (
        <EditableText tag="h2" html={props.title} editable={!!isEditable}
          onSave={(v) => save({ ...props, title: v })}
          style={{ textAlign: 'center', marginBottom: '40px', fontSize: '1.8rem', fontWeight: 700, color: '#0f172a' }}
        />
      )}
      {items.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
          No FAQs yet — edit in the properties panel.
        </p>
      ) : (
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          {items.map((item, i) => (
            <details
              key={i}
              style={{
                borderBottom: '1px solid #e2e8f0',
                padding: '4px 0',
              }}
            >
              <summary
                style={{
                  cursor: 'pointer',
                  padding: '14px 0',
                  fontWeight: 600,
                  color: '#0f172a',
                  fontSize: '0.95rem',
                  listStyle: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <EditableText tag="span" html={item.question || ''} editable={!!isEditable}
                  onSave={(v) => { const newItems = items.map((x,j) => j===i ? {...x, question: v} : x); save({...props, items: newItems}); }}
                  style={{ flex: 1, fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}
                />
                <span style={{ fontSize: '1.2rem', fontWeight: 300, marginLeft: '8px', flexShrink: 0 }}>+</span>
              </summary>
              <EditableText tag="p" html={item.answer || ''} editable={!!isEditable}
                onSave={(v) => { const newItems = items.map((x,j) => j===i ? {...x, answer: v} : x); save({...props, items: newItems}); }}
                style={{ margin: '0 0 14px', color: '#475569', lineHeight: 1.6, fontSize: '0.9rem', paddingLeft: '4px' }}
              />
            </details>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

function QuizRenderer({ props, primary, brandTheme }: { props: Record<string, any>; primary: string; brandTheme?: BrandTheme }) {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(700);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!e.data || typeof e.data !== 'object') return;
      if (e.data.type === 'aime-quiz-resize' && e.data.quizId === props.quiz_id) {
        setIframeHeight(e.data.height);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [props.quiz_id]);

  if (props.quiz_id) {
    const quizSecondary = brandTheme?.secondaryColor || '';
    const quizAccent = brandTheme?.accentColor || '';
    const font = brandTheme?.fontFamily || '';
    const params = new URLSearchParams({ primary_color: primary });
    if (quizSecondary) params.set('secondary_color', quizSecondary);
    if (quizAccent) params.set('accent_color', quizAccent);
    if (font) params.set('font_family', font);
    const quizSrc = `/api/landing-pages/quiz-embed/${props.quiz_id}?${params.toString()}`;
    return (
      <div className="lps lps-quiz" style={{ ...sectionBase, background: `${primary}0d`, padding: '48px 24px', textAlign: 'center', ...buildSectionStyle(props) }}>
        {props.title && (
          <h2 style={{ margin: '0 0 24px', fontSize: '1.8rem', fontWeight: 700, color: '#0f172a' }}>
            {props.title}
          </h2>
        )}
        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
          <iframe
            ref={iframeRef}
            src={quizSrc}
            style={{ width: '100%', height: `${iframeHeight}px`, border: 'none', borderRadius: '12px', transition: 'height 0.3s ease' }}
            title={props.quiz_name || 'Quiz'}
          />
        </div>
      </div>
    );
  }
  return (
    <div className="lps lps-quiz" style={{ ...sectionBase, background: `${primary}0d`, border: `2px dashed ${primary}44`, padding: '56px 48px', textAlign: 'center', ...buildSectionStyle(props) }}>
      <h2 style={{ margin: '0 0 12px', fontSize: '1.8rem', fontWeight: 700, color: '#0f172a' }}>
        {props.title || 'Interactive Quiz'}
      </h2>
      <p style={{ color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>
        No quiz assigned — select a quiz in the properties panel.
      </p>
    </div>
  );
}

function LeadFormRenderer({ props, primary, secondary }: { props: Record<string, any>; primary: string; secondary: string }) {
  const [linkedForm, setLinkedForm] = useState<any>(null);
  const [loadingForm, setLoadingForm] = useState(false);

  useEffect(() => {
    const formId = props.form_id;
    if (!formId) {
      setLinkedForm(null);
      return;
    }
    setLoadingForm(true);
    getLeadForm(formId)
      .then((data) => setLinkedForm(data))
      .catch(() => setLinkedForm(null))
      .finally(() => setLoadingForm(false));
  }, [props.form_id]);

  // Determine which fields to render
  const formFields: Array<{ id: string; label: string; type: string; placeholder: string; required: boolean; options?: string[] }> =
    linkedForm?.fields && linkedForm.fields.length > 0
      ? linkedForm.fields
      : null;

  const submitLabel = linkedForm?.submit_button_text || props.submit_label || 'Submit';
  const successMessage = linkedForm?.success_message || '';
  const formName = linkedForm?.name || '';

  return (
    <div
      className="lps lps-lead-form"
      id="lead-form"
      style={{
        ...sectionBase,
        background: '#ffffff',
        padding: '60px 0',
        ...buildSectionStyle(props),
      }}
    >
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {props.title && (
        <h2
          style={{
            textAlign: 'center',
            marginBottom: '8px',
            fontSize: '1.8rem',
            fontWeight: 700,
            color: '#0f172a',
          }}
        >
          {props.title}
        </h2>
      )}
      {props.subtitle && (
        <p style={{ color: '#64748b', marginBottom: '32px', textAlign: 'center' }}>
          {props.subtitle}
        </p>
      )}
      {props.form_id && formName && (
        <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '12px', textAlign: 'center' }}>
          Linked form: {formName}
        </p>
      )}
      <div
        style={{
          width: '100%',
          maxWidth: '480px',
          opacity: 0.7,
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        {loadingForm ? (
          <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#64748b' }}>Loading form fields...</p>
        ) : formFields ? (
          /* Render real fields from linked Lead Capture Form */
          formFields.map((field) => (
            <div key={field.id}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#374151',
                }}
              >
                {field.label}{field.required ? ' *' : ''}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  disabled
                  placeholder={field.placeholder || field.label}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    background: '#f8fafc',
                    boxSizing: 'border-box' as const,
                    color: '#374151',
                    resize: 'vertical' as const,
                    fontFamily: 'inherit',
                  }}
                />
              ) : field.type === 'select' ? (
                <select
                  disabled
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    background: '#f8fafc',
                    boxSizing: 'border-box' as const,
                    color: '#374151',
                  }}
                >
                  <option value="">-- Select --</option>
                  {(field.options || []).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <input
                  disabled
                  type={field.type || 'text'}
                  placeholder={field.placeholder || field.label}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    background: '#f8fafc',
                    boxSizing: 'border-box' as const,
                    color: '#374151',
                  }}
                />
              )}
            </div>
          ))
        ) : (
          /* Default fallback fields when no form is linked */
          ['Full Name', 'Email Address', 'Phone Number'].map((label) => (
            <div key={label}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#374151',
                }}
              >
                {label}
              </label>
              <input
                disabled
                placeholder={label}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  background: '#f8fafc',
                  boxSizing: 'border-box' as const,
                  color: '#374151',
                }}
              />
            </div>
          ))
        )}
        <button
          disabled
          className="lp-btn"
          style={{
            width: '100%',
            padding: '12px',
            background: props.button_color || secondary,
            color: '#ffffff',
            border: 'none',
            borderRadius: '9999px',
            fontWeight: 700,
            fontSize: '1rem',
            marginTop: '6px',
            cursor: 'not-allowed',
          }}
        >
          {submitLabel}
        </button>
        {successMessage && (
          <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#22c55e', margin: 0 }}>
            Success message: "{successMessage}"
          </p>
        )}
        <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>
          Preview only — form will be active when published
        </p>
      </div>
      </div>
    </div>
  );
}

function CtaRendererButtonMode({ props, primary, secondary, isEditable, onTextSave }: { props: Record<string, any>; primary: string; secondary: string; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  const save = onTextSave ?? (() => {});
  return (
    <div
      className="lps lps-cta"
      style={{
        ...sectionBase,
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
        color: '#ffffff',
        padding: '72px 0',
        textAlign: 'center',
        ...buildSectionStyle(props),
      }}
    >
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px' }}>
      <EditableText tag="h2" html={props.title || 'Ready to Get Started?'} editable={!!isEditable}
        onSave={(v) => save({ ...props, title: v })}
        style={{ margin: '0 0 12px', fontSize: '2rem', fontWeight: 800 }}
      />
      {(props.subtitle || isEditable) && (
        <EditableText tag="p" html={props.subtitle || ''} editable={!!isEditable}
          onSave={(v) => save({ ...props, subtitle: v })}
          style={{ margin: '0 0 28px', fontSize: '1.1rem', opacity: 0.8 }}
        />
      )}
      {props.button_text && (
        <a
          href={props.button_url || '#'}
          className="lp-btn"
          style={{
            display: 'inline-block',
            background: props.button_color || secondary,
            color: '#ffffff',
            padding: '14px 36px',
            borderRadius: '9999px',
            textDecoration: 'none',
            fontWeight: 700,
            fontSize: '1rem',
            boxShadow: `0 4px 20px ${props.button_shadow_color || secondary}80`,
          }}
        >
          {props.button_text}
        </a>
      )}
      </div>
    </div>
  );
}

function CtaBannerWithForm({ props, primary, secondary, isEditable, onTextSave }: { props: Record<string, any>; primary: string; secondary: string; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  const save = onTextSave ?? (() => {});
  const [linkedForm, setLinkedForm] = useState<any>(null);
  const [loadingForm, setLoadingForm] = useState(false);

  useEffect(() => {
    const formId = props.form_id;
    if (!formId) { setLinkedForm(null); return; }
    setLoadingForm(true);
    getLeadForm(formId)
      .then((data) => setLinkedForm(data))
      .catch(() => setLinkedForm(null))
      .finally(() => setLoadingForm(false));
  }, [props.form_id]);

  // Defensive fallback — render button-mode JSX if form_id missing or fetch failed
  if (!props.form_id || (!loadingForm && !linkedForm)) {
    return <CtaRendererButtonMode props={props} primary={primary} secondary={secondary} isEditable={isEditable} onTextSave={save} />;
  }

  const formFields: Array<{ id: string; label: string; type: string; placeholder: string; required: boolean; options?: string[] }> | null =
    linkedForm?.fields && linkedForm.fields.length > 0 ? linkedForm.fields : null;

  const submitLabel = props.submit_button_text || linkedForm?.submit_button_text || 'Submit';
  const formName = linkedForm?.name || '';
  const formTitle = props.form_title || props.title || 'Get Started';
  const successMessage = props.success_message || linkedForm?.success_message || '';

  const inputStyle: React.CSSProperties = {
    display: 'block', width: '100%', boxSizing: 'border-box',
    marginBottom: '12px', padding: '10px 14px',
    border: '1px solid #e2e8f0', borderRadius: '6px',
    fontSize: '0.875rem', color: '#64748b', background: '#f8fafc',
  };

  return (
    <div
      className="lps lps-cta lps-cta-with-form"
      style={{
        ...sectionBase,
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
        color: '#ffffff',
        padding: '72px 0',
        textAlign: 'center',
        ...buildSectionStyle(props),
      }}
    >
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px' }}>
        <EditableText tag="h2" html={props.title || 'Ready to Get Started?'} editable={!!isEditable}
          onSave={(v) => save({ ...props, title: v })}
          style={{ margin: '0 0 12px', fontSize: '2rem', fontWeight: 800 }}
        />
        {(props.subtitle || isEditable) && (
          <EditableText tag="p" html={props.subtitle || ''} editable={!!isEditable}
            onSave={(v) => save({ ...props, subtitle: v })}
            style={{ margin: '0 0 28px', fontSize: '1.1rem', opacity: 0.8 }}
          />
        )}
        <form
          id="cta-form"
          className="lps-cta-form"
          data-form-id={props.form_id}
          data-success-message={successMessage}
          action="javascript:void(0)"
          style={{
            background: 'rgba(255,255,255,0.97)',
            color: '#0f172a',
            borderRadius: '12px',
            padding: '32px',
            boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
            maxWidth: '520px',
            margin: '0 auto',
            textAlign: 'left',
          }}
        >
          <h3 style={{ margin: '0 0 8px', fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>{formTitle}</h3>
          {formName && (
            <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '-4px 0 16px' }}>Linked form: {formName}</p>
          )}
          {loadingForm && <p style={{ color: '#64748b', fontSize: '0.875rem', textAlign: 'center' }}>Loading form fields…</p>}
          {!loadingForm && formFields && (
            formFields.map((field) => (
              <div key={field.id}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>
                  {field.label}{field.required ? ' *' : ''}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    name={field.id}
                    placeholder={field.placeholder || field.label}
                    required={field.required}
                    readOnly={!!isEditable}
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                  />
                ) : field.type === 'select' ? (
                  <select
                    name={field.id}
                    required={field.required}
                    disabled={!!isEditable}
                    style={inputStyle}
                  >
                    <option value="">-- Select --</option>
                    {(field.options || []).map((opt: string) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    name={field.id}
                    type={field.type || 'text'}
                    placeholder={field.placeholder || field.label}
                    required={field.required}
                    readOnly={!!isEditable}
                    style={inputStyle}
                  />
                )}
              </div>
            ))
          )}
          {!loadingForm && !formFields && (
            ['Full Name', 'Email Address', 'Phone Number'].map((field) => (
              <input
                key={field}
                placeholder={field}
                readOnly
                style={inputStyle}
              />
            ))
          )}
          <button
            type="submit"
            className="lp-btn"
            style={{
              background: props.button_color || secondary,
              color: '#ffffff',
              padding: '14px 36px',
              borderRadius: '9999px',
              border: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              width: '100%',
              cursor: isEditable ? 'default' : 'pointer',
              marginTop: '12px',
            }}
          >
            {submitLabel}
          </button>
          {isEditable && (
            <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#94a3b8', margin: '8px 0 0' }}>
              (submits live on the published page)
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

function CtaRenderer({ props, primary, secondary, isEditable, onTextSave }: { props: Record<string, any>; primary: string; secondary: string; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  const mode = props.display_mode || 'button';
  if (mode === 'inline_form') {
    return <CtaBannerWithForm props={props} primary={primary} secondary={secondary} isEditable={isEditable} onTextSave={onTextSave} />;
  }
  return <CtaRendererButtonMode props={props} primary={primary} secondary={secondary} isEditable={isEditable} onTextSave={onTextSave} />;
}

function PricingRenderer({ props, primary, secondary }: { props: Record<string, any>; primary: string; secondary: string }) {
  const tiers: Array<{ name: string; price: string; period?: string; features: string[]; cta_text: string; cta_url: string; highlighted: boolean }> =
    props.items || [];
  const btnRadius = props.pill_buttons === false ? '8px' : '9999px';

  return (
    <div
      className="lps lps-pricing"
      style={{
        ...sectionBase,
        background: '#ffffff',
        padding: '60px 0',
        textAlign: 'center',
        ...buildSectionStyle(props),
      }}
    >
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px' }}>
      {props.title && (
        <h2 style={{ marginBottom: '12px', fontSize: '1.8rem', fontWeight: 700, color: '#0f172a' }}>
          {props.title}
        </h2>
      )}
      {props.subtitle && (
        <p style={{ marginBottom: '36px', color: '#64748b', fontSize: '1rem' }}>{props.subtitle}</p>
      )}
      {tiers.length === 0 ? (
        <div style={{ border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '48px 32px', color: '#94a3b8', fontStyle: 'italic' }}>
          Add pricing tiers in the properties panel
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(tiers.length, 3)}, 1fr)`,
          gap: '24px',
          maxWidth: '960px',
          margin: '0 auto',
          alignItems: 'stretch',
        }}>
          {tiers.map((tier, i) => (
            <div key={i} style={{
              background: tier.highlighted ? `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` : '#ffffff',
              color: tier.highlighted ? '#ffffff' : '#0f172a',
              border: tier.highlighted ? 'none' : '1px solid #e2e8f0',
              borderRadius: '16px',
              padding: '36px 28px',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              boxShadow: tier.highlighted ? '0 8px 32px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
              transform: tier.highlighted ? 'scale(1.04)' : 'none',
            }}>
              {tier.highlighted && (
                <div style={{
                  position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
                  background: secondary, color: '#fff', padding: '4px 16px',
                  borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>
                  Most Popular
                </div>
              )}
              <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700 }}>{tier.name}</h3>
              <div style={{ margin: '0 0 20px' }}>
                <span style={{ fontSize: '2.4rem', fontWeight: 800 }}>{tier.price}</span>
                {tier.period && <span style={{ fontSize: '0.9rem', opacity: 0.7 }}>/{tier.period}</span>}
              </div>
              <ul style={{
                listStyle: 'none', padding: 0, margin: '0 0 24px', textAlign: 'left',
                flex: 1, display: 'flex', flexDirection: 'column', gap: '10px',
              }}>
                {(tier.features || []).map((f, fi) => (
                  <li key={fi} style={{ fontSize: '0.875rem', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: tier.highlighted ? '#ffffff' : secondary, fontWeight: 700, flexShrink: 0 }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href={tier.cta_url || '#lead-form'}
                className="lp-btn"
                style={{
                  display: 'block', textAlign: 'center', padding: '12px 24px',
                  background: tier.highlighted ? '#ffffff' : secondary,
                  color: tier.highlighted ? primary : '#ffffff',
                  borderRadius: btnRadius,
                  textDecoration: 'none', fontWeight: 700, fontSize: '0.9rem',
                }}
              >
                {tier.cta_text || 'Get Started'}
              </a>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

function LogoCloudRenderer({ props, primary }: { props: Record<string, any>; primary: string }) {
  const logos: Array<{ name: string; logo_url: string; width?: number }> = props.logos || [];
  return (
    <div
      style={{
        ...sectionBase,
        background: '#f8fafc',
        padding: '48px 0',
        textAlign: 'center',
        ...buildSectionStyle(props),
      }}
    >
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px' }}>
      {props.title && (
        <p
          style={{
            marginBottom: '24px',
            fontWeight: 600,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: '0.75rem',
          }}
        >
          {props.title}
        </p>
      )}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '24px',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {logos.length === 0 ? (
          [...Array(4)].map((_, i) => (
            <div
              key={i}
              style={{
                width: '120px',
                height: '48px',
                border: '2px dashed #cbd5e1',
                borderRadius: '8px',
                background: '#ffffff',
              }}
            />
          ))
        ) : (
          logos.map((logo, i) =>
            logo.logo_url ? (
              <img
                key={i}
                src={logo.logo_url}
                alt={logo.name}
                style={{ height: '48px', width: logo.width ? `${logo.width}px` : 'auto', objectFit: 'contain', opacity: 0.7 }}
              />
            ) : (
              <div
                key={i}
                style={{
                  width: logo.width ? `${logo.width}px` : '120px',
                  height: '48px',
                  border: '2px dashed #cbd5e1',
                  borderRadius: '8px',
                  background: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  color: '#94a3b8',
                }}
              >
                {logo.name || 'Logo'}
              </div>
            )
          )
        )}
      </div>
      {logos.length === 0 && (
        <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: '16px', fontStyle: 'italic' }}>
          Add logos in the properties panel
        </p>
      )}
      </div>
    </div>
  );
}

function VideoRenderer({ props, primary }: { props: Record<string, any>; primary: string }) {
  const isEmbedType = (url: string) => /youtube\.com|youtu\.be|vimeo\.com/.test(url);

  return (
    <div
      style={{
        ...sectionBase,
        background: primary,
        padding: '48px',
        textAlign: 'center',
        ...buildSectionStyle(props),
      }}
    >
      {props.title && (
        <h2 style={{ marginBottom: '24px', fontSize: '1.6rem', fontWeight: 700, color: '#ffffff' }}>
          {props.title}
        </h2>
      )}
      <div
        style={{
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto',
          aspectRatio: '5 / 2',
          position: 'relative',
          background: '#1e293b',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {props.video_url ? (
          isEmbedType(props.video_url) ? (
            <iframe
              src={toEmbedUrl(props.video_url)}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
              allowFullScreen
              title={props.title || 'Video'}
            />
          ) : (
            <video
              src={props.video_url}
              controls
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )
        ) : (
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 0, height: 0, borderTop: '12px solid transparent', borderBottom: '12px solid transparent', borderLeft: '20px solid #ffffff', marginLeft: '4px' }} />
            </div>
          </div>
        )}
      </div>
      {!props.video_url && (
        <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '12px' }}>No video URL set — add one in the properties panel.</p>
      )}
    </div>
  );
}

// ── New component renderers ─────────────────────────────────────────────────

function ScarcityTimerRenderer({ props, primary }: { props: Record<string, any>; primary: string }) {
  const bg = props.background_color || '#e53e3e';
  const showDays: boolean = props.show_days !== false;
  const redirectUrl = props.expired_redirect || '/offer-expired';

  const calcRemaining = (): { d: number; h: number; m: number; s: number } => {
    if (!props.end_datetime) return { d: 3, h: 0, m: 0, s: 0 };
    const diff = Math.max(0, Math.floor((new Date(props.end_datetime).getTime() - Date.now()) / 1000));
    const d = Math.floor(diff / 86400);
    const h = Math.floor((diff % 86400) / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    return { d, h, m, s };
  };

  const [remaining, setRemaining] = useState(calcRemaining);
  const isExpired = props.end_datetime && new Date(props.end_datetime).getTime() <= Date.now();

  const buildRedirectUrl = (base: string) => {
    const origin = document.referrer;
    if (!origin) return base;
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}from=${encodeURIComponent(origin)}`;
  };

  useEffect(() => {
    const id = setInterval(() => setRemaining(calcRemaining()), 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.end_datetime]);

  const pad = (n: number) => String(n).padStart(2, '0');

  const unitBoxStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  };
  const numStyle: React.CSSProperties = {
    fontSize: '2.8rem',
    fontWeight: 800,
    color: '#ffffff',
    lineHeight: 1,
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '8px',
    padding: '10px 18px',
    minWidth: '72px',
    textAlign: 'center',
  };
  const unitLabelStyle: React.CSSProperties = {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.85)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  };
  const sepStyle: React.CSSProperties = {
    fontSize: '2.5rem',
    fontWeight: 700,
    color: 'rgba(255,255,255,0.7)',
    alignSelf: 'flex-start',
    marginTop: '8px',
  };

  return (
    <div
      data-aime-timer
      data-end={props.end_datetime || ''}
      data-redirect={props.expired_redirect || '/offer-expired'}
      data-expired-msg={props.expired_message || 'This offer has expired.'}
      data-show-days={showDays ? '1' : '0'}
      style={{
        ...sectionBase,
        background: bg,
        padding: '40px 48px',
        textAlign: 'center',
        color: '#ffffff',
        ...buildSectionStyle(props),
      }}
    >
      {props.title && (
        <h2 style={{ margin: '0 0 6px', fontSize: '1.6rem', fontWeight: 800, color: '#ffffff' }}>
          {props.title}
        </h2>
      )}
      {props.subtitle && (
        <p style={{ margin: '0 0 24px', fontSize: '1rem', color: 'rgba(255,255,255,0.85)' }}>
          {props.subtitle}
        </p>
      )}
      {isExpired ? (
        <p data-aime-timer-expired style={{ fontSize: '1.1rem', fontStyle: 'italic', color: 'rgba(255,255,255,0.9)' }}>
          {props.expired_message || 'This offer has expired.'}
        </p>
      ) : (
        <>
          <div
            data-aime-timer-display
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            {showDays && (
              <>
                <div style={unitBoxStyle}>
                  <div data-aime-timer-d style={numStyle}>{pad(remaining.d)}</div>
                  <span style={unitLabelStyle}>Days</span>
                </div>
                <span style={sepStyle}>:</span>
              </>
            )}
            <div style={unitBoxStyle}>
              <div data-aime-timer-h style={numStyle}>{pad(remaining.h)}</div>
              <span style={unitLabelStyle}>Hours</span>
            </div>
            <span style={sepStyle}>:</span>
            <div style={unitBoxStyle}>
              <div data-aime-timer-m style={numStyle}>{pad(remaining.m)}</div>
              <span style={unitLabelStyle}>Mins</span>
            </div>
            <span style={sepStyle}>:</span>
            <div style={unitBoxStyle}>
              <div data-aime-timer-s style={numStyle}>{pad(remaining.s)}</div>
              <span style={unitLabelStyle}>Secs</span>
            </div>
          </div>
          <p style={{ marginTop: '20px', fontSize: '0.85rem', color: 'rgba(255,255,255,0.75)', fontWeight: 600, letterSpacing: '0.04em' }}>
            This offer expires — act now
          </p>
        </>
      )}
    </div>
  );
}

function VslRenderer({ props, primary, brandTheme }: { props: Record<string, any>; primary: string; brandTheme?: BrandTheme }) {
  const [showCta, setShowCta] = useState(props.show_cta_after_seconds === 0);

  useEffect(() => {
    if (props.show_cta_after_seconds > 0) {
      // In the editor, always show CTA for preview purposes
      setShowCta(true);
    }
  }, [props.show_cta_after_seconds]);

  const isYouTubeOrVimeo = (url: string) => /youtube\.com|youtu\.be|vimeo\.com/.test(url);

  return (
    <div
      style={{
        ...sectionBase,
        background: primary,
        padding: '48px',
        textAlign: 'center',
        color: '#ffffff',
        ...buildSectionStyle(props),
      }}
    >
      {props.title && (
        <h2 style={{ margin: '0 0 28px', fontSize: '1.8rem', fontWeight: 800, color: '#ffffff' }}>
          {props.title}
        </h2>
      )}
      <div
        style={{
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto',
          aspectRatio: '5 / 2',
          position: 'relative',
          background: '#1e293b',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {props.video_url ? (
          isYouTubeOrVimeo(props.video_url) ? (
            <iframe
              src={toEmbedUrl(props.video_url)}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
              allowFullScreen
              title="VSL Video"
            />
          ) : (
            <video
              src={props.video_url}
              poster={props.poster_url || undefined}
              autoPlay={props.autoplay}
              controls={!props.hide_controls}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )
        ) : (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  width: 0,
                  height: 0,
                  borderTop: '14px solid transparent',
                  borderBottom: '14px solid transparent',
                  borderLeft: '22px solid #ffffff',
                  marginLeft: '5px',
                }}
              />
            </div>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
              No video URL set — add one in properties
            </span>
          </div>
        )}
      </div>
      {showCta && props.cta_text && (() => {
        const btnDefault = props.button_color || brandTheme?.secondaryColor || primary;
        const btnHover = brandTheme?.accentColor || brandTheme?.secondaryColor || primary;
        return (
          <a
            href={props.cta_url || '#'}
            className="lp-btn"
            style={{
              display: 'inline-block',
              marginTop: '32px',
              background: btnDefault,
              color: '#ffffff',
              padding: '16px 40px',
              borderRadius: '9999px',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '1.1rem',
              boxShadow: `0 4px 20px ${btnDefault}44`,
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = btnHover)}
            onMouseLeave={e => (e.currentTarget.style.background = btnDefault)}
          >
            {props.cta_text}
          </a>
        );
      })()}
      {props.show_cta_after_seconds > 0 && (
        <p style={{ marginTop: '8px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
          CTA appears after {props.show_cta_after_seconds}s when published (shown in preview)
        </p>
      )}
    </div>
  );
}

function StockRenderer({ props, primary }: { props: Record<string, any>; primary: string }) {
  const priceId: string | undefined = props.stripe_price_id;

  // Live Stripe stock — only fetched when a price_id is configured
  const [liveStock, setLiveStock] = useState<{ current: number; max: number; soldOut: boolean } | null>(null);

  useEffect(() => {
    if (!priceId) return;
    const fetchStock = () => {
      fetch(`/api/landing-pages/stock?price_id=${encodeURIComponent(priceId)}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) setLiveStock({
            current: data.stock_available ?? 0,
            max: data.stock_quantity ?? 0,
            soldOut: data.sold_out,
          });
        })
        .catch(() => {});
    };
    fetchStock();
    const interval = setInterval(fetchStock, 60000);
    return () => clearInterval(interval);
  }, [priceId]);

  // Use live data when available, fall back to static props
  const current: number = liveStock ? liveStock.current : (props.current_stock ?? 7);
  const max: number = liveStock ? liveStock.max : (props.max_stock ?? 20);
  const soldOut: boolean = liveStock ? liveStock.soldOut : false;
  const threshold: number = props.urgency_threshold ?? 5;
  const isUrgent = !soldOut && current <= threshold;
  const pct = max > 0 ? Math.min(100, Math.round((current / max) * 100)) : 0;
  const barColor = isUrgent ? '#e53e3e' : primary;

  return (
    <div
      style={{
        ...sectionBase,
        background: '#ffffff',
        padding: '48px',
        textAlign: 'center',
        ...buildSectionStyle(props),
      }}
    >
      {props.title && (
        <h2 style={{ margin: '0 0 24px', fontSize: '1.6rem', fontWeight: 700, color: '#0f172a' }}>
          {props.title}
        </h2>
      )}

      {soldOut ? (
        /* ── Sold Out state ─────────────────────────────────────── */
        <div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '10px',
            background: '#fee2e2', border: '2px solid #e53e3e',
            borderRadius: '9999px', padding: '10px 28px', marginBottom: '12px',
          }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#e53e3e', flexShrink: 0, display: 'inline-block' }} />
            <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#e53e3e' }}>
              {props.sold_out_label || 'Sold Out'}
            </span>
          </div>
          {props.sold_out_message && (
            <p style={{ margin: '8px 0 0', fontSize: '0.9rem', color: '#64748b' }}>{props.sold_out_message}</p>
          )}
        </div>
      ) : (
        /* ── In-stock state ─────────────────────────────────────── */
        <>
          {props.show_progress_bar !== false && (
            <div style={{ maxWidth: '480px', margin: '0 auto 20px', textAlign: 'left' }}>
              <div style={{ height: '12px', background: '#e2e8f0', borderRadius: '9999px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: '9999px', transition: 'width 0.3s ease' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>0</span>
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{max}</span>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            {isUrgent && (
              <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', background: '#e53e3e', flexShrink: 0, boxShadow: '0 0 0 4px rgba(229,62,62,0.25)' }} />
            )}
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: isUrgent ? '#e53e3e' : '#0f172a' }}>
              {current} {props.unit_label || 'spots remaining'}
            </p>
          </div>
          {isUrgent && (
            <p style={{ marginTop: '8px', fontSize: '0.875rem', color: '#e53e3e', fontWeight: 600 }}>
              Almost gone — act fast!
            </p>
          )}
          {priceId && liveStock && (
            <p style={{ marginTop: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>Live availability</p>
          )}
        </>
      )}
    </div>
  );
}

function ExitPopupRenderer({ props, primary, secondary }: { props: Record<string, any>; primary: string; secondary: string }) {
  return (
    <div
      data-aime-exit-popup
      data-once={props.show_once_per_session ? '1' : '0'}
      style={{
        ...sectionBase,
        background: '#f8fafc',
        padding: '24px 48px',
      }}
    >
      <div
        data-aime-exit-card
        style={{
          border: '2px dashed #cbd5e1',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '560px',
          margin: '0 auto',
          background: '#ffffff',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
      >
        <div
          data-aime-exit-badge
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: '#fef3c7',
            color: '#92400e',
            fontSize: '0.7rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            padding: '3px 10px',
            borderRadius: '9999px',
            marginBottom: '20px',
          }}
        >
          Exit-Intent Popup Preview
        </div>
        <h2
          style={{
            margin: '0 0 12px',
            fontSize: '1.5rem',
            fontWeight: 800,
            color: '#0f172a',
            lineHeight: 1.3,
          }}
        >
          {props.headline || "Wait — Don't Leave Yet!"}
        </h2>
        <p style={{ margin: '0 0 24px', color: '#475569', lineHeight: 1.6, fontSize: '0.95rem' }}>
          {props.body_text}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <a
            href={props.cta_url || '#'}
            className="lp-btn"
            style={{
              display: 'block',
              textAlign: 'center',
              background: props.button_color || secondary,
              color: '#ffffff',
              padding: '14px 24px',
              borderRadius: '9999px',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
            }}
          >
            {props.cta_text || 'Yes, Book My Free Consultation'}
          </a>
          <button
            disabled
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              fontSize: '0.8rem',
              cursor: 'not-allowed',
              textDecoration: 'underline',
              padding: '4px',
            }}
          >
            {props.dismiss_label || "No thanks, I'll pass"}
          </button>
        </div>
        <p
          data-aime-exit-hint
          style={{
            marginTop: '16px',
            fontSize: '0.75rem',
            color: '#94a3b8',
            fontStyle: 'italic',
            textAlign: 'center',
            margin: '16px 0 0',
          }}
        >
          This popup will trigger on exit-intent when published.
          {props.show_once_per_session ? ' Shown once per session.' : ''}
        </p>
      </div>
    </div>
  );
}

function SocialProofPopupRenderer({ props, primary }: { props: Record<string, any>; primary: string }) {
  const notifications: Array<{ name: string; action: string; location: string; time_ago: string }> =
    props.notifications || [];
  const first = notifications[0];
  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((p) => p[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  return (
    <div
      style={{
        ...sectionBase,
        background: '#f8fafc',
        padding: '24px 48px',
      }}
    >
      <div
        style={{
          border: '2px dashed #cbd5e1',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '480px',
          margin: '0 auto',
          background: '#ffffff',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: '#dbeafe',
            color: '#1e40af',
            fontSize: '0.7rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            padding: '3px 10px',
            borderRadius: '9999px',
            marginBottom: '16px',
          }}
        >
          Social Proof Notification Preview
        </div>
        {first && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              background: '#f8fafc',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              marginBottom: '16px',
            }}
          >
            {props.show_avatar !== false && (
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: primary,
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  flexShrink: 0,
                }}
              >
                {getInitials(first.name)}
              </div>
            )}
            <div>
              <p style={{ margin: 0, fontWeight: 600, color: '#0f172a', fontSize: '0.875rem' }}>
                <strong>{first.name}</strong> {first.action}
              </p>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                {first.location} · {first.time_ago}
              </p>
            </div>
          </div>
        )}
        <div>
          <p style={{ margin: '0 0 8px', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            All configured notifications:
          </p>
          {notifications.map((n, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '6px 0',
                borderBottom: i < notifications.length - 1 ? '1px solid #f1f5f9' : 'none',
                fontSize: '0.8rem',
                color: '#475569',
              }}
            >
              <span><strong>{n.name}</strong> {n.action} · {n.location}</span>
              <span style={{ color: '#94a3b8', flexShrink: 0, marginLeft: '8px' }}>{n.time_ago}</span>
            </div>
          ))}
          {notifications.length === 0 && (
            <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.8rem' }}>
              No notifications configured — add some in properties.
            </p>
          )}
        </div>
        <p style={{ marginTop: '12px', fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic', margin: '12px 0 0' }}>
          These notifications will cycle every {props.display_interval_seconds ?? 5}s when published.
        </p>
      </div>
    </div>
  );
}

function AuthorityRenderer({ props, primary }: { props: Record<string, any>; primary: string }) {
  const logos: Array<{ name: string; logo_url: string; width?: number }> = props.logos || [];
  const credentials: Array<{ icon: string; label: string }> = props.credentials || [];

  return (
    <div
      style={{
        ...sectionBase,
        background: '#ffffff',
        padding: '56px 48px',
        textAlign: 'center',
        ...buildSectionStyle(props),
      }}
    >
      {props.title && (
        <h2 style={{ margin: '0 0 8px', fontSize: '1.6rem', fontWeight: 700, color: '#0f172a' }}>
          {props.title}
        </h2>
      )}
      {props.subtitle && (
        <p style={{ margin: '0 0 32px', color: '#64748b', fontSize: '1rem' }}>
          {props.subtitle}
        </p>
      )}
      {props.show_logos !== false && (
        <div style={{ marginBottom: '36px' }}>
          <p style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>
            Trusted By
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '16px' }}>
            {logos.length > 0
              ? logos.map((logo, i) => (
                  <img
                    key={i}
                    src={logo.logo_url}
                    alt={logo.name}
                    style={{ height: '40px', width: logo.width ? `${logo.width}px` : 'auto', objectFit: 'contain', opacity: 0.7, filter: 'grayscale(100%)' }}
                  />
                ))
              : [...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: '100px',
                      height: '40px',
                      border: '2px dashed #cbd5e1',
                      borderRadius: '6px',
                      background: '#f8fafc',
                    }}
                  />
                ))}
          </div>
          {logos.length === 0 && (
            <p style={{ color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic', marginTop: '8px' }}>
              Add logo URLs in the properties panel
            </p>
          )}
        </div>
      )}
      {props.show_credentials !== false && credentials.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px' }}>
          {credentials.map((cred, i) => (
            <div
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '9999px',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  background: `${primary}1a`,
                  color: primary,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}
              >
                {renderLpIcon(cred.icon || 'check-circle', 12, primary)}
              </span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>{cred.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RoiCalculatorRenderer({ props, primary, brandTheme }: { props: Record<string, any>; primary: string; brandTheme?: BrandTheme }) {
  const inputs: Array<{ id: string; label: string; type: 'slider' | 'number'; min: number; max: number; default: number; prefix: string; suffix: string }> =
    props.inputs || [];

  const initialValues: Record<string, number> = {};
  inputs.forEach((inp) => { initialValues[inp.id] = inp.default ?? 0; });

  const [values, setValues] = useState<Record<string, number>>(initialValues);

  const calcResult = (): number => {
    if (!props.formula) return 0;
    try {
      const argNames = inputs.map((inp) => inp.id);
      const argValues = inputs.map((inp) => values[inp.id] ?? inp.default ?? 0);
      // eslint-disable-next-line no-new-func
      return new Function(...argNames, `return (${props.formula})`)(
        ...argValues
      ) as number;
    } catch {
      return 0;
    }
  };

  const result = calcResult();
  const formatNum = (n: number) =>
    isNaN(n) || !isFinite(n)
      ? '—'
      : Math.abs(n) >= 1000
      ? n.toLocaleString('en-US', { maximumFractionDigits: 0 })
      : n.toFixed(2);

  const setVal = (id: string, v: number) => setValues((prev) => ({ ...prev, [id]: v }));

  const secondary = brandTheme?.secondaryColor || primary;
  const accent = brandTheme?.accentColor || secondary;

  return (
    <div
      data-aime-roi
      data-formula={props.formula || ''}
      data-inputs={JSON.stringify(inputs)}
      data-output-prefix={props.output_prefix ?? ''}
      data-output-suffix={props.output_suffix ?? ''}
      data-accent={accent}
      data-secondary={secondary}
      style={{
        ...sectionBase,
        background: '#ffffff',
        padding: '60px 48px',
        textAlign: 'center',
        ...buildSectionStyle(props),
      }}
    >
      {props.title && (
        <h2 style={{ margin: '0 0 8px', fontSize: '1.8rem', fontWeight: 700, color: '#0f172a' }}>
          {props.title}
        </h2>
      )}
      {props.subtitle && (
        <p style={{ margin: '0 0 36px', color: '#64748b', fontSize: '1rem' }}>
          {props.subtitle}
        </p>
      )}
      <div style={{ maxWidth: '560px', margin: '0 auto' }}>
        {inputs.map((inp) => (
          <div key={inp.id} style={{ marginBottom: '24px', textAlign: 'left' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
              {inp.label}
              {inp.prefix || inp.suffix ? (
                <span data-aime-roi-label={inp.id} style={{ fontWeight: 400, color: '#64748b', marginLeft: '4px' }}>
                  ({inp.prefix}{values[inp.id] ?? inp.default}{inp.suffix})
                </span>
              ) : null}
            </label>
            {inp.type === 'slider' ? (
              <div>
                <input
                  type="range"
                  data-aime-roi-input={inp.id}
                  min={inp.min}
                  max={inp.max}
                  value={values[inp.id] ?? inp.default}
                  onChange={(e) => setVal(inp.id, Number(e.target.value))}
                  style={{ width: '100%', accentColor: secondary }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94a3b8' }}>
                  <span>{inp.prefix}{inp.min}{inp.suffix}</span>
                  <span>{inp.prefix}{inp.max}{inp.suffix}</span>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {inp.prefix && <span style={{ fontSize: '0.9rem', color: '#374151', fontWeight: 600 }}>{inp.prefix}</span>}
                <input
                  type="number"
                  data-aime-roi-input={inp.id}
                  min={inp.min}
                  max={inp.max}
                  value={values[inp.id] ?? inp.default}
                  onChange={(e) => setVal(inp.id, Number(e.target.value))}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: `1px solid ${secondary}33`,
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    color: '#0f172a',
                    background: '#f8fafc',
                    boxSizing: 'border-box' as const,
                  }}
                />
                {inp.suffix && <span style={{ fontSize: '0.9rem', color: '#64748b' }}>{inp.suffix}</span>}
              </div>
            )}
          </div>
        ))}
        <div
          data-aime-roi-result-box
          style={{
            background: props.result_bg_color || `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`,
            borderRadius: '12px',
            padding: '28px',
            textAlign: 'center',
            marginBottom: '24px',
          }}
        >
          <p style={{ margin: '0 0 4px', fontSize: '0.8rem', color: props.result_text_color ? `${props.result_text_color}99` : 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
            {props.output_label || 'Estimated Annual Benefit'}
          </p>
          <p data-aime-roi-result style={{ margin: 0, fontSize: '2.8rem', fontWeight: 800, color: props.result_text_color || '#ffffff' }}>
            {props.output_prefix ?? ''}{formatNum(result)}{props.output_suffix ?? ''}
          </p>
        </div>
        {props.cta_text && (
          <a
            href={props.cta_url || '#'}
            className="lp-btn"
            style={{
              display: 'block',
              background: secondary,
              color: '#ffffff',
              padding: '14px 32px',
              borderRadius: '9999px',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              boxShadow: `0 4px 20px ${secondary}44`,
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = accent)}
            onMouseLeave={e => (e.currentTarget.style.background = secondary)}
          >
            {props.cta_text}
          </a>
        )}
      </div>
    </div>
  );
}

function LogoPicker({ currentUrl, logoOptions, onSelect, dark }: {
  currentUrl: string;
  logoOptions: BrandLogoOption[];
  onSelect: (url: string) => void;
  dark?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  if (logoOptions.length < 2) return null;
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        title="Change logo"
        style={{
          position: 'absolute', bottom: '-20px', left: '50%', transform: 'translateX(-50%)',
          background: '#2177FF', color: '#fff', border: 'none', borderRadius: '9999px',
          fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', cursor: 'pointer',
          whiteSpace: 'nowrap', zIndex: 20, letterSpacing: '0.03em',
        }}
      >
        Change logo
      </button>
      {open && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 24px)', left: '50%',
            transform: 'translateX(-50%)',
            background: dark ? 'rgba(15,23,42,0.97)' : 'rgba(255,255,255,0.98)',
            border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.12)'}`,
            borderRadius: '10px', padding: '10px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            display: 'flex', flexDirection: 'column', gap: '6px',
            zIndex: 30, minWidth: '160px',
          }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
        >
          {logoOptions.map((opt) => (
            <button
              key={opt.url}
              onClick={() => { onSelect(opt.url); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: opt.url === currentUrl
                  ? (dark ? 'rgba(33,119,255,0.2)' : '#EAF3FF')
                  : 'transparent',
                border: `1px solid ${opt.url === currentUrl ? '#2177FF' : 'transparent'}`,
                borderRadius: '7px', padding: '6px 8px', cursor: 'pointer', textAlign: 'left',
              }}
            >
              <div style={{
                width: '48px', height: '28px', flexShrink: 0, borderRadius: '4px',
                background: dark ? '#1e293b' : '#f1f5f9',
                display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
              }}>
                <img src={opt.url} alt={opt.label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>
              <span style={{ fontSize: '0.72rem', fontWeight: 500, color: dark ? '#e2e8f0' : '#374151' }}>
                {opt.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function HeaderRenderer({ props, primary, secondary, brandTheme, isEditable, onTextSave }: { props: Record<string, any>; primary: string; secondary: string; brandTheme?: BrandTheme; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  const logoUrl = props.logo_url || brandTheme?.logoUrl || '';
  const companyName = props.company_name || brandTheme?.companyName || 'Your Company';
  const navLinks: Array<{ label: string; url: string }> = props.nav_links || [];
  const save = onTextSave ?? (() => {});
  const logoOptions = brandTheme?.logoOptions || (logoUrl ? [{ url: logoUrl, label: 'Logo' }] : []);

  return (
    <header
      className="lps lps-header"
      style={{
        ...sectionBase,
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        position: 'relative',
        ...buildSectionStyle(props),
      }}
    >
      <div className="lps-header-bar" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '72px',
        width: '100%',
        boxSizing: 'border-box',
      }}>
      {/* Logo / brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative', paddingBottom: isEditable ? '18px' : 0 }}>
        {logoUrl ? (
          <img src={logoUrl} alt={companyName} style={{ height: '40px', objectFit: 'contain' }} />
        ) : (
          <div style={{
            height: '40px', display: 'flex', alignItems: 'center',
            fontWeight: 800, fontSize: '1.3rem', color: primary, letterSpacing: '-0.02em',
          }}>
            {companyName}
          </div>
        )}
        {isEditable && (
          <LogoPicker
            currentUrl={logoUrl}
            logoOptions={logoOptions}
            onSelect={(url) => save({ ...props, logo_url: url })}
          />
        )}
      </div>

      {/* Desktop nav links */}
      {navLinks.length > 0 && (
        <nav className="lps-nav" style={{ display: 'flex', gap: '28px', alignItems: 'center' }}>
          {navLinks.map((link, i) => (
            <a key={i} href={link.url} style={{ textDecoration: 'none', color: '#374151', fontSize: '0.9rem', fontWeight: 500 }}>
              {link.label}
            </a>
          ))}
        </nav>
      )}

      {/* Desktop phone */}
      {props.phone && (
        <a className="lps-phone" href={`tel:${props.phone}`} style={{ color: '#374151', textDecoration: 'none', fontSize: '0.875rem', fontWeight: 500 }}>
          {props.phone}
        </a>
      )}

      {/* Desktop CTA */}
      {props.cta_text && (
        <a
          href={props.cta_url || '#lead-form'}
          className="lp-btn lps-desktop-cta"
          style={{
            display: 'inline-block',
            background: props.button_color || secondary,
            color: '#ffffff',
            padding: '9px 22px',
            borderRadius: '9999px',
            textDecoration: 'none',
            fontWeight: 700,
            fontSize: '0.875rem',
          }}
        >
          {props.cta_text}
        </a>
      )}

      {/* Hamburger button (mobile only, hidden on desktop via CSS) */}
      <button
        className="lps-hamburger"
        aria-label="Menu"
        style={{
          display: 'none', /* shown via @media */
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          lineHeight: 1,
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      </div>

      {/* Mobile dropdown menu (hidden by default, toggled by hamburger JS) */}
      <div className="lps-mobile-menu" style={{
        display: 'none',
        flexDirection: 'column',
        gap: '0',
        padding: '0 16px 16px',
        borderTop: '1px solid #e2e8f0',
        background: '#ffffff',
      }}>
        {navLinks.map((link, i) => (
          <a key={i} href={link.url} style={{
            display: 'block', padding: '12px 0',
            textDecoration: 'none', color: '#374151',
            fontSize: '0.9rem', fontWeight: 500,
            borderBottom: '1px solid #f1f5f9',
          }}>
            {link.label}
          </a>
        ))}
        {props.phone && (
          <a href={`tel:${props.phone}`} style={{
            display: 'block', padding: '12px 0',
            textDecoration: 'none', color: '#374151',
            fontSize: '0.9rem', fontWeight: 500,
            borderBottom: '1px solid #f1f5f9',
          }}>
            {props.phone}
          </a>
        )}
        {props.cta_text && (
          <a
            href={props.cta_url || '#lead-form'}
            className="lp-btn"
            style={{
              display: 'block', textAlign: 'center', marginTop: '12px',
              background: props.button_color || secondary,
              color: '#ffffff', padding: '12px 24px',
              borderRadius: '9999px', textDecoration: 'none',
              fontWeight: 700, fontSize: '0.9rem',
            }}
          >
            {props.cta_text}
          </a>
        )}
      </div>
    </header>
  );
}

function FooterRenderer({ props, primary, brandTheme, isEditable, onTextSave }: { props: Record<string, any>; primary: string; brandTheme?: BrandTheme; isEditable?: boolean; onTextSave?: (p: Record<string, any>) => void }) {
  const links: Array<{ label: string; url: string }> = props.links || [];
  const hasContactInfo = props.phone || props.email || props.address || props.opening_hours;
  const save = onTextSave ?? (() => {});
  const logoUrl = props.logo_url || brandTheme?.logoUrl || '';
  const logoOptions = brandTheme?.logoOptions || (logoUrl ? [{ url: logoUrl, label: 'Logo' }] : []);

  return (
    <footer
      className="lps lps-footer"
      style={{
        ...sectionBase,
        background: '#0f172a',
        color: '#94a3b8',
        padding: '56px 48px 32px',
        ...buildSectionStyle(props),
      }}
    >
      <div style={{ maxWidth: '1080px', margin: '0 auto' }}>
        {/* Top section — 3 columns: Brand | Contact | Links */}
        <div className="lps-footer-grid" style={{ display: 'grid', gridTemplateColumns: hasContactInfo ? '2fr 1.5fr 1.5fr' : '2fr 1fr', gap: '48px', marginBottom: '48px' }}>
          {/* Column 1: Brand */}
          <div>
            <div style={{ position: 'relative', display: 'inline-block', paddingBottom: isEditable ? '20px' : 0 }}>
              {logoUrl ? (
                <img src={logoUrl} alt={props.company_name || 'Logo'}
                  style={{ height: '44px', objectFit: 'contain', marginBottom: '16px', display: 'block' }} />
              ) : (
                <div style={{ fontWeight: 800, fontSize: '1.3rem', color: '#ffffff', marginBottom: '16px' }}>
                  {props.company_name || 'Your Company'}
                </div>
              )}
              {isEditable && (
                <LogoPicker
                  currentUrl={logoUrl}
                  logoOptions={logoOptions}
                  onSelect={(url) => save({ ...props, logo_url: url })}
                  dark
                />
              )}
            </div>
            {props.tagline && (
              <p style={{ margin: '0 0 0 0', fontSize: '0.875rem', lineHeight: 1.7, maxWidth: '280px' }}>
                {props.tagline}
              </p>
            )}
          </div>

          {/* Column 2: Contact info */}
          {hasContactInfo && (
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#ffffff', marginBottom: '16px' }}>
                Contact
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.875rem' }}>
                {props.address && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <MapPin size={14} style={{ flexShrink: 0, marginTop: '3px', opacity: 0.6 }} />
                    <span style={{ whiteSpace: 'pre-line' }}>{props.address}</span>
                  </div>
                )}
                {props.phone && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Phone size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
                    <a href={`tel:${props.phone}`} style={{ color: '#94a3b8', textDecoration: 'none' }}>{props.phone}</a>
                  </div>
                )}
                {props.email && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Mail size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
                    <a href={`mailto:${props.email}`} style={{ color: '#94a3b8', textDecoration: 'none' }}>{props.email}</a>
                  </div>
                )}
                {props.opening_hours && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <Clock size={14} style={{ flexShrink: 0, marginTop: '3px', opacity: 0.6 }} />
                    <span style={{ whiteSpace: 'pre-line' }}>{props.opening_hours}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Column 3: Links */}
          {links.length > 0 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#ffffff', marginBottom: '16px' }}>
                Quick Links
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {links.map((link, i) => (
                  <a key={i} href={link.url} style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom row */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '24px', fontSize: '0.8rem', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <span>{props.copyright || `© ${new Date().getFullYear()} ${props.company_name || 'Your Company'}. All rights reserved.`}</span>
        </div>
      </div>
    </footer>
  );
}

// ── Product buy section ──────────────────────────────────────────────────────

function ProductRenderer({ props, primary, secondary }: { props: Record<string, any>; primary: string; secondary: string }) {
  const price = props.variant_price ?? 0;
  const currency = props.currency || 'USD';
  const symbol = currency === 'USD' ? '$' : currency === 'AUD' ? 'A$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency;
  const formattedPrice = `${symbol}${Number(price).toFixed(2)}`;
  const buyUrl = props.variant_id ? `/api/shop/buy/${props.variant_id}` : '#';
  const hasProduct = !!props.product_id;

  return (
    <div style={{ background: props.background_color || '#ffffff', padding: '60px 0' }}>
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px' }}>
        {props.title && (
          <h2 style={{ fontSize: '2rem', fontWeight: 800, textAlign: 'center', color: props.title_color || '#0f172a', marginBottom: '8px' }}>
            {props.title}
          </h2>
        )}
        {props.subtitle && (
          <p style={{ textAlign: 'center', color: props.subtitle_color || '#64748b', fontSize: '1.05rem', marginBottom: '40px' }}>
            {props.subtitle}
          </p>
        )}
        {!hasProduct ? (
          <div style={{ border: '2px dashed #cbd5e1', padding: '48px 32px', textAlign: 'center', color: '#94a3b8', borderRadius: '12px' }}>
            Select a product in the properties panel to display it here.
          </div>
        ) : (
          <div style={{
            display: 'flex',
            gap: '40px',
            alignItems: 'flex-start',
            maxWidth: '800px',
            margin: '0 auto',
            flexWrap: 'wrap' as const,
          }}>
            {props.product_image && (
              <div style={{ flex: '0 0 280px', maxWidth: '280px' }}>
                <img
                  src={props.product_image}
                  alt={props.product_name}
                  style={{ width: '100%', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', display: 'block' }}
                />
              </div>
            )}
            <div style={{ flex: '1 1 260px' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: props.name_color || '#0f172a', marginBottom: '12px' }}>
                {props.product_name}
              </h3>
              {props.product_description && (
                <p style={{ color: props.description_color || '#475569', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '24px' }}>
                  {props.product_description}
                </p>
              )}
              <div style={{ fontSize: '2.25rem', fontWeight: 800, color: props.price_color || primary, marginBottom: '20px' }}>
                {formattedPrice}
                {props.variant_label && (
                  <span style={{ fontSize: '1rem', fontWeight: 500, color: '#94a3b8', marginLeft: '8px' }}>
                    {props.variant_label}
                  </span>
                )}
              </div>
              <a
                href={buyUrl}
                className="lp-btn"
                style={{
                  display: 'inline-block',
                  padding: '14px 32px',
                  background: props.button_color || primary,
                  color: props.button_text_color || '#ffffff',
                  borderRadius: '9999px',
                  fontWeight: 700,
                  fontSize: '1rem',
                  textDecoration: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  marginBottom: '16px',
                }}
              >
                {props.button_text || 'Buy Now'}
              </a>
              {props.show_guarantee && props.guarantee_text && (
                <p style={{ fontSize: '0.8rem', color: props.guarantee_color || '#94a3b8', marginTop: '8px' }}>
                  🔒 {props.guarantee_text}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Whitepaper download section ──────────────────────────────────────────────
// Product-backed like ProductRenderer, but delivers a PDF instead of selling
// a variant. Ungated renders a real anchor to the public download route
// (verified live at product_routes.py:3528, GET /api/shop/whitepaper/{id}/download).
// Gated renders a preview of the lead-capture form that gates the download —
// the same "disabled inputs / Preview only" pattern as LeadFormRenderer,
// because the real submission only exists on the published page (an
// <aime-lead-form> web component wired up by LandingPageEditor.tsx's publish
// step, carrying data-aime-component-id so submit_lead can resolve the
// product server-side — see specs/completed/whitepaper-module.md Phase 4).
function WhitepaperRenderer({ props, primary, secondary }: { props: Record<string, any>; primary: string; secondary: string }) {
  const hasProduct = !!props.product_id;
  const gated = props.gated !== false;
  const downloadUrl = props.product_id ? `/api/shop/whitepaper/${props.product_id}/download` : '#';
  const buttonLabel = props.button_label || 'Download Now';

  return (
    <div style={{ background: '#ffffff', padding: '60px 0' }}>
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        {props.heading && (
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
            {props.heading}
          </h2>
        )}
        {props.subheading && (
          <p style={{ color: '#64748b', fontSize: '1.05rem', marginBottom: '32px', maxWidth: '560px' }}>
            {props.subheading}
          </p>
        )}
        {!hasProduct ? (
          <div style={{ border: '2px dashed #cbd5e1', padding: '48px 32px', textAlign: 'center', color: '#94a3b8', borderRadius: '12px', width: '100%', maxWidth: '480px' }}>
            Select a whitepaper in the properties panel to display it here.
          </div>
        ) : !gated ? (
          <>
            {props.cover_image_override && (
              <img
                src={props.cover_image_override}
                alt={props.heading || 'Whitepaper cover'}
                style={{ width: '220px', borderRadius: '8px', boxShadow: '0 4px 24px rgba(0,0,0,0.12)', marginBottom: '24px' }}
              />
            )}
            <a
              href={downloadUrl}
              className="lp-btn"
              style={{
                display: 'inline-block',
                padding: '14px 32px',
                background: primary,
                color: '#ffffff',
                borderRadius: '9999px',
                fontWeight: 700,
                fontSize: '1rem',
                textDecoration: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {buttonLabel}
            </a>
          </>
        ) : (
          <div
            style={{
              width: '100%',
              maxWidth: '480px',
              opacity: 0.7,
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
            }}
          >
            {props.cover_image_override && (
              <img
                src={props.cover_image_override}
                alt={props.heading || 'Whitepaper cover'}
                style={{ width: '160px', borderRadius: '8px', margin: '0 auto 8px', boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}
              />
            )}
            {['Full Name', 'Email Address'].map((label) => (
              <div key={label}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>
                  {label}
                </label>
                <input
                  disabled
                  placeholder={label}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    background: '#f8fafc',
                    boxSizing: 'border-box' as const,
                    color: '#374151',
                  }}
                />
              </div>
            ))}
            <button
              disabled
              className="lp-btn"
              style={{
                width: '100%',
                padding: '12px',
                background: secondary,
                color: '#ffffff',
                border: 'none',
                borderRadius: '9999px',
                fontWeight: 700,
                fontSize: '1rem',
                marginTop: '6px',
                cursor: 'not-allowed',
              }}
            >
              {buttonLabel}
            </button>
            <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>
              Preview only — form will be active when published
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Social share section ─────────────────────────────────────────────────────

function ShareRenderer({ props, primary }: { props: Record<string, any>; primary: string }) {
  const platforms: string[] = props.platforms || ['facebook', 'twitter', 'linkedin', 'whatsapp'];
  const shareText = encodeURIComponent(props.share_text || 'Check this out:');
  const btnStyle = props.button_style || 'filled';

  const platformConfig: Record<string, { label: string; color: string; bg: string; icon: string }> = {
    facebook:  { label: 'Facebook',    color: '#fff', bg: '#1877F2', icon: 'f' },
    twitter:   { label: 'X (Twitter)', color: '#fff', bg: '#000000', icon: '𝕏' },
    linkedin:  { label: 'LinkedIn',    color: '#fff', bg: '#0A66C2', icon: 'in' },
    whatsapp:  { label: 'WhatsApp',   color: '#fff', bg: '#25D366', icon: '' },
    email:     { label: 'Email',       color: '#fff', bg: '#64748b', icon: '✉' },
  };

  const shareUrls: Record<string, string> = {
    facebook:  `https://www.facebook.com/sharer/sharer.php?u=PAGE_URL`,
    twitter:   `https://twitter.com/intent/tweet?url=PAGE_URL&text=${shareText}`,
    linkedin:  `https://www.linkedin.com/sharing/share-offsite/?url=PAGE_URL`,
    whatsapp:  `https://wa.me/?text=${shareText}%20PAGE_URL`,
    email:     `mailto:?subject=${shareText}&body=${shareText}%20PAGE_URL`,
  };

  const buttonEl = (platform: string) => {
    const cfg = platformConfig[platform];
    if (!cfg) return null;
    const isFilled = btnStyle === 'filled';
    const isIconOnly = btnStyle === 'icon_only';
    return (
      <a
        key={platform}
        href={shareUrls[platform]}
        target="_blank"
        rel="noopener noreferrer"
        className="lp-btn"
        data-share-platform={platform}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: isIconOnly ? '0' : '8px',
          padding: isIconOnly ? '12px' : '10px 20px',
          borderRadius: isIconOnly ? '50%' : '9999px',
          background: isFilled ? cfg.bg : 'transparent',
          color: isFilled ? cfg.color : cfg.bg,
          border: `2px solid ${cfg.bg}`,
          fontWeight: 600,
          fontSize: '0.875rem',
          textDecoration: 'none',
          cursor: 'pointer',
          minWidth: isIconOnly ? '44px' : undefined,
          justifyContent: 'center',
        }}
      >
        <span style={{ fontWeight: 800, fontSize: isIconOnly ? '1rem' : '0.8rem', lineHeight: 1 }}>{cfg.icon}</span>
        {!isIconOnly && <span>{cfg.label}</span>}
      </a>
    );
  };

  return (
    <div style={{ background: '#f8fafc', padding: '60px 0' }}>
      <div className="lps-inner" style={{ ...innerWrap, padding: '0 48px', textAlign: 'center' }}>
        {props.title && (
          <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
            {props.title}
          </h2>
        )}
        {props.subtitle && (
          <p style={{ color: '#64748b', fontSize: '1rem', marginBottom: '32px' }}>
            {props.subtitle}
          </p>
        )}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' as const, marginBottom: props.show_copy_link ? '20px' : '0' }}>
          {platforms.map(buttonEl)}
        </div>
        {props.show_copy_link && (
          <button
            onClick={() => {
              navigator.clipboard?.writeText(window.location.href);
            }}
            className="lp-btn"
            style={{
              marginTop: '12px',
              padding: '8px 20px',
              borderRadius: '9999px',
              background: 'transparent',
              border: `2px solid ${primary}`,
              color: primary,
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer',
            }}
          >
            Copy Link
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

const LPComponentRenderer: React.FC<LPComponentRendererProps> = ({ component, isSelected, brandTheme, onPropsChange }) => {
  const { type, props } = component;
  const primary   = brandTheme?.primaryColor   || '#2177FF';
  const secondary = brandTheme?.secondaryColor || '#1EACA6';
  const accent    = brandTheme?.accentColor    || secondary;
  const editable = isSelected;
  const onTextSave = onPropsChange
    ? (newProps: Record<string, any>) => onPropsChange(component.id, newProps)
    : undefined;

  useEffect(() => {
    const id = 'lp-brand-theme-styles';
    let el = document.getElementById(id) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = [
      `.lp-btn { transition: background 0.2s ease, box-shadow 0.2s ease; }`,
      `.lp-btn:hover { background: ${accent} !important; box-shadow: 0 4px 20px ${accent}66 !important; }`,
    ].join('\n');
  }, [accent]);

  const renderSection = () => {
  switch (type) {
    case 'lp_hero':
      return <HeroRenderer props={props} primary={primary} secondary={secondary} brandTheme={brandTheme} isEditable={editable} onTextSave={onTextSave} />;
    case 'lp_features':
      return <FeaturesRenderer props={props} primary={primary} isEditable={editable} onTextSave={onTextSave} />;
    case 'lp_services':
      return <ServicesRenderer props={props} primary={primary} isEditable={editable} onTextSave={onTextSave} />;
    case 'lp_stats':
      return <StatsRenderer props={props} primary={primary} isEditable={editable} onTextSave={onTextSave} />;
    case 'lp_process':
      return <ProcessRenderer props={props} primary={primary} isEditable={editable} onTextSave={onTextSave} />;
    case 'lp_testimonials':
      return <TestimonialsRenderer props={props} primary={primary} isEditable={editable} onTextSave={onTextSave} />;
    case 'lp_faq':
      return <FaqRenderer props={props} primary={primary} isEditable={editable} onTextSave={onTextSave} />;
    case 'lp_quiz':
      return <QuizRenderer props={props} primary={primary} brandTheme={brandTheme} />;
    case 'lp_lead_form':
      return <LeadFormRenderer props={props} primary={primary} secondary={secondary} />;
    case 'lp_cta':
      return <CtaRenderer props={props} primary={primary} secondary={secondary} isEditable={editable} onTextSave={onTextSave} />;
    case 'lp_pricing':
      return <PricingRenderer props={props} primary={primary} secondary={secondary} />;
    case 'lp_logo_cloud':
      return <LogoCloudRenderer props={props} primary={primary} />;
    case 'lp_video':
      return <VideoRenderer props={props} primary={primary} />;
    case 'lp_scarcity_timer':
      return <ScarcityTimerRenderer props={props} primary={primary} />;
    case 'lp_vsl':
      return <VslRenderer props={props} primary={primary} brandTheme={brandTheme} />;
    case 'lp_stock':
      return <StockRenderer props={props} primary={primary} />;
    case 'lp_exit_popup':
      return <ExitPopupRenderer props={props} primary={primary} secondary={secondary} />;
    case 'lp_social_proof_popup':
      return <SocialProofPopupRenderer props={props} primary={primary} />;
    case 'lp_authority':
      return <AuthorityRenderer props={props} primary={primary} />;
    case 'lp_roi_calculator':
      return <RoiCalculatorRenderer props={props} primary={primary} brandTheme={brandTheme} />;
    case 'lp_header':
      return <HeaderRenderer props={props} primary={primary} secondary={secondary} brandTheme={brandTheme} isEditable={editable} onTextSave={onTextSave} />;
    case 'lp_footer':
      return <FooterRenderer props={props} primary={primary} brandTheme={brandTheme} isEditable={editable} onTextSave={onTextSave} />;
    case 'lp_product':
      return <ProductRenderer props={props} primary={primary} secondary={secondary} />;
    case 'lp_whitepaper':
      return <WhitepaperRenderer props={props} primary={primary} secondary={secondary} />;
    case 'lp_share':
      return <ShareRenderer props={props} primary={primary} />;
    case 'lp_compliance_banner':
      return <LpComplianceBannerRenderer props={props} />;
    case 'lp_cookie_consent_banner':
      return <LpCookieConsentBannerRenderer props={props} />;
    case 'lp_text':
      return <LpTextRenderer props={props} isEditable={editable} onTextSave={onTextSave} />;
    case 'lp_effect':
      return <LpEffectRenderer props={props} primary={primary} isEditable={editable} onTextSave={onTextSave} />;
    default:
      return (
        <div
          style={{
            padding: '32px',
            background: '#f8fafc',
            border: '2px dashed #cbd5e1',
            color: '#94a3b8',
            textAlign: 'center',
          }}
        >
          Unknown section type: {type}
        </div>
      );
  }
  }; // end renderSection

  const sectionId = type.replace(/^lp_/, '');
  return <div id={sectionId} style={{ scrollMarginTop: '80px' }}>{renderSection()}</div>;
};

// ---------------------------------------------------------------------------
// Compliance banner renderers
// ---------------------------------------------------------------------------

const _LP_DS: React.CSSProperties = {
  fontSize: '0.75rem', color: 'rgba(255,255,255,0.60)', margin: 0,
  paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.10)', lineHeight: 1.5,
};
const _LP_LINK: React.CSSProperties = { color: '#c9a23b', textDecoration: 'underline' };
const _LP_BTN_PRI: React.CSSProperties = {
  padding: '8px 20px', borderRadius: 6, border: 'none',
  background: '#2563eb', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem',
};
const _LP_BTN_SEC: React.CSSProperties = {
  padding: '8px 20px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.3)',
  background: 'transparent', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem',
};

function _LpD({ text }: { text: string }) {
  return <p style={_LP_DS}>{text}</p>;
}

function LpComplianceBannerRenderer({ props: p }: { props: Record<string, any> }) {
  const jur = p.jurisdiction || 'australia';
  const holder = p.license_holder || '';
  const showRisk = p.show_risk_disclaimer ?? true;
  const riskText = p.risk_disclaimer_text || 'All investments carry risk. Past performance is not a reliable indicator of future performance.';
  const nodes: React.ReactNode[] = [];

  const reg = (...parts: (string | false | undefined)[]) => {
    const s = parts.filter(Boolean).join(' | ');
    if (s) nodes.push(<_LpD key="reg" text={s} />);
  };

  if (jur === 'australia') {
    const afsl = p.afsl_number || '';
    reg(afsl && `AFSL: ${afsl}`, !afsl && holder ? holder : afsl && holder ? holder : undefined);
    if (showRisk) nodes.push(<_LpD key="risk" text={riskText} />);
    if (p.show_wholesale_notice) nodes.push(<_LpD key="w" text={p.wholesale_notice_text || 'This information is provided to wholesale clients only.'} />);
    if (p.show_past_performance) nodes.push(<_LpD key="pp" text={p.past_performance_text || 'Past performance is not a reliable indicator of future performance.'} />);
    if (p.show_anti_hawking) nodes.push(<_LpD key="ah" text={p.anti_hawking_text || 'This document does not constitute an offer in any jurisdiction where it would not be lawful.'} />);
  } else if (jur === 'usa') {
    reg(p.sec_ria_number && `SEC/RIA CRD: ${p.sec_ria_number}`, p.finra_crd && `FINRA CRD: ${p.finra_crd}`, holder);
    if (showRisk) nodes.push(<_LpD key="risk" text={riskText} />);
    if (p.show_investment_advice_notice ?? true) nodes.push(<_LpD key="ia" text={p.investment_advice_text || 'This material is for informational purposes only and does not constitute investment advice.'} />);
  } else if (jur === 'uk') {
    reg(p.fca_number && `FCA Ref: ${p.fca_number}`, holder);
    if (showRisk) nodes.push(<_LpD key="risk" text={riskText} />);
    if (p.show_past_performance) nodes.push(<_LpD key="pp" text={p.past_performance_text || 'Past performance is not a reliable indicator of future performance.'} />);
  } else if (jur === 'eu') {
    reg(p.mifid_auth_number && `MiFID II Auth: ${p.mifid_auth_number}`, p.nca_name && `Supervised by ${p.nca_name}`, holder);
    if (showRisk) nodes.push(<_LpD key="risk" text={riskText} />);
    if (p.show_past_performance) nodes.push(<_LpD key="pp" text={p.past_performance_text || 'Past performance is not a reliable indicator of future performance.'} />);
  } else if (jur === 'singapore') {
    reg(p.mas_cms_number && `MAS CMS: ${p.mas_cms_number}`, holder);
    if (showRisk) nodes.push(<_LpD key="risk" text={riskText} />);
    if (p.show_accredited_investor) nodes.push(<_LpD key="acc" text={p.accredited_investor_text || 'This material is directed at accredited investors only.'} />);
  } else if (jur === 'hong_kong') {
    const sfcType = p.sfc_license_type || 'Type 1';
    reg(p.sfc_license_number ? `SFC ${sfcType}: ${p.sfc_license_number}` : `SFC ${sfcType}`, holder);
    if (showRisk) nodes.push(<_LpD key="risk" text={riskText} />);
  } else if (jur === 'japan') {
    reg(p.fsa_registration_number && `FSA Reg: ${p.fsa_registration_number}`, holder);
    if (showRisk) nodes.push(<_LpD key="risk" text={riskText} />);
  } else if (jur === 'india') {
    reg(p.sebi_registration_number && `SEBI Reg: ${p.sebi_registration_number}`, holder);
    if (showRisk) nodes.push(<_LpD key="risk" text={riskText} />);
  } else if (jur === 'south_korea') {
    reg(p.fsc_registration_number && `FSC Reg: ${p.fsc_registration_number}`, holder);
    if (showRisk) nodes.push(<_LpD key="risk" text={riskText} />);
  } else {
    reg(p.other_license_number && `Licence: ${p.other_license_number}`, p.regulatory_body && `Regulated by ${p.regulatory_body}`, holder);
    if (showRisk) nodes.push(<_LpD key="risk" text={riskText} />);
  }

  return (
    <aside style={{ background: '#1e293b', color: '#fff', padding: '16px 24px', display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
      <p style={{ flex: 1, margin: 0, fontSize: '0.875rem', minWidth: 200 }}>
        {p.message || 'We use cookies to improve your experience.'}{' '}
        <a href={p.privacy_url || '/privacy'} style={_LP_LINK}>Privacy Policy</a>
      </p>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button style={_LP_BTN_PRI}>{p.accept_text || 'Accept All'}</button>
        <button style={_LP_BTN_SEC}>{p.decline_text || 'Decline'}</button>
      </div>
      {nodes.length > 0 && <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>{nodes}</div>}
    </aside>
  );
}

function LpCookieConsentBannerRenderer({ props: p }: { props: Record<string, any> }) {
  const manage = p.manage_text || '';
  const cookieUrl = p.cookie_policy_url || '';
  return (
    <aside style={{ background: '#1e293b', color: '#fff', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <p style={{ flex: 1, margin: 0, fontSize: '0.875rem', minWidth: 200 }}>
        {p.message || 'We use cookies to improve your experience.'}{' '}
        <a href={p.privacy_url || '/privacy'} style={_LP_LINK}>Privacy Policy</a>
        {cookieUrl && <> &amp; <a href={cookieUrl} style={_LP_LINK}>Cookie Policy</a></>}
      </p>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        {manage && <button style={_LP_BTN_SEC}>{manage}</button>}
        <button style={_LP_BTN_SEC}>{p.decline_text || 'Decline'}</button>
        <button style={_LP_BTN_PRI}>{p.accept_text || 'Accept All'}</button>
      </div>
    </aside>
  );
}

export default LPComponentRenderer;
